/**
 * 화면별 도움말 원문.
 *
 * 헤더의 ‘? 도움말’ 이 현재 경로로 여기서 항목을 찾아 오른쪽 드로어에 편다.
 * 담는 것은 네 가지다 — 이 화면에서 하는 일, 화면에 **실제로 있는** 버튼 이름과 설명,
 * 기본 사용 순서, 먼저 채워 둬야 하는 기준정보 화면 링크.
 *
 * 규칙
 * - ‘controls.name’ 은 화면에 그려지는 글자와 정확히 같아야 한다. 버튼 이름을 바꾸면 여기도 바꾼다.
 * - ‘screenshot’ 은 ‘scripts/capture-help-shots.mjs’ 가 만든 ‘frontend/public/help/*.png’ 를 가리킨다.
 *   파일이 없으면 드로어가 그 자리를 조용히 접는다.
 * - 도메인 규칙(상행 = 포스코·수재·송장 없음 / 하행 = 회차마다 송장 2장)은 화면마다 되풀이하지 않고
 *   해당 화면에서 실제로 걸리는 지점에만 적는다.
 */

export interface HelpControl {
  /** 화면에 그려지는 버튼·탭 이름 그대로 */
  name: string;
  description: string;
}

export interface HelpLink {
  label: string;
  path: string;
  /** 왜 이 화면을 함께 보는지 */
  reason: string;
}

export interface HelpTopic {
  /** react-router 경로 패턴. ‘:id’ 같은 동적 구간을 포함한다 */
  pattern: string;
  title: string;
  summary: string;
  /** 탭 화면은 탭마다 한 장씩 여러 개를 준다 */
  screenshot?: string | string[];
  steps: string[];
  controls: HelpControl[];
  notes: string[];
  related: HelpLink[];
}

// ---------------------------------------------------------------------------
// 자주 쓰는 링크
// ---------------------------------------------------------------------------

const L = {
  vehicle: {
    label: "기준정보 · 차량·기사",
    path: "/admin/master/vehicle",
    reason: "차량에 붙는 기사·업체가 여기 배정 이력에서 옵니다",
  },
  location: {
    label: "기준정보 · 장소",
    path: "/admin/master/location",
    reason: "상차지·하차지 드롭다운의 원천입니다",
  },
  route: {
    label: "기준정보 · 구간",
    path: "/admin/master/route",
    reason: "상차지→하차지 쌍과 기본 품목을 정의합니다",
  },
  item: {
    label: "기준정보 · 품목",
    path: "/admin/master/item",
    reason: "품목 드롭다운과 원본명 매핑의 원천입니다",
  },
  company: {
    label: "기준정보 · 거래처",
    path: "/admin/master/company",
    reason: "운송회사·청구처·상품소유업체 드롭다운의 원천입니다",
  },
  price: {
    label: "기준정보 · 단가",
    path: "/admin/master/price",
    reason: "금액은 전부 여기 단가에서 계산됩니다",
  },
  plan: {
    label: "기준정보 · 월간 계획",
    path: "/admin/master/plan",
    reason: "실적 화면의 계획 열이 여기서 옵니다",
  },
  profile: {
    label: "기준정보 · 자사정보",
    path: "/admin/master/profile",
    reason: "명세서 공급자 칸과 직인이 여기서 옵니다",
  },
  dispatch: {
    label: "배차 현황",
    path: "/admin/dispatch",
    reason: "모든 실적·집계·정산이 배차에서 파생됩니다",
  },
  // 조회가 아니라 **등록·수정**이 목적인 상호참조용. 배차현황은 보기만 하는 화면이다.
  dispatchOrders: {
    label: "배차 등록",
    path: "/admin/dispatch/orders",
    reason: "기사·업체 배차를 여기서 만들고 고칩니다",
  },
  invoice: {
    label: "송장 제출현황",
    path: "/admin/invoice",
    reason: "하행 실적은 송장 검증으로 확정됩니다",
  },
  performance: {
    label: "일일실적 현황",
    path: "/admin/performance",
    reason: "확정된 실적이 집계되는 화면입니다",
  },
  driverAccounts: {
    label: "기사관리 · 기사 계정",
    path: "/admin/drivers/accounts",
    reason: "기사 로그인 계정과 비밀번호를 관리합니다",
  },
  disputes: {
    label: "기사관리 · 이의신청 회신",
    path: "/admin/drivers/disputes",
    reason: "기사가 정산에 제기한 이의를 처리합니다",
  },
  vehicleChanges: {
    label: "기사관리 · 차량변경 승인",
    path: "/admin/drivers/vehicle-changes",
    reason: "승인하면 배정 이력이 자동으로 만들어집니다",
  },
  notices: {
    label: "기사관리 · 공지 관리",
    path: "/admin/drivers/notices",
    reason: "기사 앱에 뿌리는 공지를 씁니다",
  },
  users: {
    label: "시스템 · 사용자 관리",
    path: "/admin/users",
    reason: "계정을 만들고 역할·상태를 관리합니다",
  },
  auditLogs: {
    label: "시스템 · 변경 이력",
    path: "/admin/audit-logs",
    reason: "누가 언제 무엇을 바꿨는지 남아 있습니다 — 지운 계정이 한 일도 그대로입니다",
  },
} satisfies Record<string, HelpLink>;

/** 표 인라인 편집은 화면마다 같은 규칙이라 문장을 한 번만 쓴다 */
const INLINE_EDIT =
  "표의 값을 직접 눌러 고칠 수 있습니다. 고치면 곧바로 저장되고, 계산으로 나오는 값은 함께 다시 계산됩니다.";

const DROPDOWN_SEARCH =
  "드롭다운은 모두 검색이 됩니다. 목록이 길면 글자를 입력해 좁히세요. 화살표·괄호·밑줄은 무시하므로 ‘포스코→한라시멘트’ 를 ‘포스코한’ 으로도 찾습니다. 띄어 쓰면 순서와 상관없이 둘 다 든 항목만 남습니다(‘단양 성신’).";

// ---------------------------------------------------------------------------
// 배차관리
// ---------------------------------------------------------------------------

const DISPATCH: HelpTopic[] = [
  {
    pattern: "/admin/dispatch",
    title: "배차 현황",
    screenshot: "/help/dispatch.png",
    summary:
      "그날 나간 배차를 기사·업체 가리지 않고 한 표로 보는 화면입니다. 자사 차량과 업체 차량이 따로 있어 ‘오늘 몇 대 나갔나’ 를 두 화면에서 더해야 했던 것을 한 자리로 모았습니다.",
    steps: [
      "날짜 이동으로 볼 날짜를 고릅니다. 처음 열면 어제가 잡혀 있습니다.",
      "구분·방향·운송사·검색으로 목록을 좁힙니다. 어느 조건이든 기사 줄과 업체 줄 양쪽에 걸립니다.",
      "고칠 것이 보이면 그 행을 누릅니다 — 기사 줄은 운행 회차 관리로, 업체 줄은 그 일자의 업체 배차 탭으로 넘어갑니다.",
    ],
    controls: [
      { name: "구분", description: "‘기사’ 는 차량 단위로 짠 자사 배차, ‘업체’ 는 업체에 구간·대수만 넘긴 배차입니다." },
      { name: "대수", description: "기사 줄은 차 한 대라 언제나 1이고, 업체 줄은 그 업체에 넘긴 차량 수입니다." },
      { name: "계획톤수 · 실적톤수", description: "예상한 양과 실제로 실은 양입니다. 기사 줄의 실적톤수는 하차 실측을, 아직 없으면 상차 실측을 보여 줍니다." },
      { name: "엑셀 다운로드", description: "지금 조회된 목록을 구분 열까지 담아 CSV 로 내려받습니다." },
      { name: "인쇄", description: "브라우저 인쇄 창을 엽니다." },
      { name: "전일 / 다음일", description: "하루씩 앞뒤로 이동합니다." },
    ],
    notes: [
      "이 화면은 보기만 합니다. 값을 고치거나 새 배차를 만드는 일은 ‘배차 등록’ 에서 합니다.",
      "행의 단위가 종류마다 다릅니다 — 기사 줄은 회차 한 건이 한 줄이라 한 차량이 상행 1줄 + 하행 N줄로 나옵니다. 업체 줄은 구간·품목마다 한 줄입니다.",
      "업체 줄의 차량·기사 칸은 비어 있습니다. 어느 차가 뛸지는 업체가 정하므로 차량 단위 배차를 만들지 않습니다.",
      "업체 배차에는 상태가 없어 상태 칸이 ‘—’ 입니다.",
      "행은 마우스로도 키보드로도 열립니다 — 탭으로 옮겨 Enter 를 누르세요.",
    ],
    related: [L.dispatchOrders, L.invoice, L.performance],
  },
  {
    pattern: "/admin/dispatch/orders",
    title: "배차 등록",
    screenshot: ["/help/dispatch-orders.png", "/help/dispatch-orders-carriers.png"],
    summary:
      "배차를 만들고 고치는 화면입니다. ‘기사 배차’ 표는 그 자체가 등록판입니다 — 자사 차량마다 상행·하행 두 줄이 늘 깔려 있고, 빈 칸에서 구간을 고르면 그 자리에서 등록됩니다. 하루에 3~4회차를 뛰는 차는 ‘회차 추가’ 로 줄을 늘립니다. ‘업체 배차’ 는 구간 하나를 고르고 그 구간을 뛸 업체와 대수를 줄로 넣습니다.",
    steps: [
      "위쪽 ‘기사 배차’ / ‘업체 배차’ 탭을 고릅니다.",
      "날짜를 그날로 맞춥니다. 기본값은 어제입니다.",
      "기사 배차 표에는 자사 차량이 차량번호·기사와 함께 상행·하행 두 줄씩 이미 깔려 있습니다.",
      "빈 칸에서 구간을 고르면 그 자리에서 등록됩니다. 상차지·하차지·품목·가실적은 그 구간에서 따라옵니다. 같은 상차지·하차지는 한 구간이라, 그 구간에 단가가 걸린 품목이 둘 이상이면 등록한 뒤 ‘품목’ 칸에서 바꿉니다.",
      "하행을 두 번 이상 뛰는 차는 차량번호 칸의 ‘회차 추가’ 를 누릅니다. 처음 누르면 상행과 하행 ‘사이에’ 경유 줄이, 다시 누르면 맨 아래 ‘하행 3’ 줄이 생깁니다. 상행이 없는 차는 한 번 더 누르면 ‘하행 4’ 줄까지 생깁니다 — 첫 구간을 수재 대신 다른 품목으로 나가 네 구간이 모두 하행인 날입니다.",
      "목록에 없는 구간은 ‘구간 빠른 등록’ 으로 그 자리에서 만듭니다. 색도 함께 고를 수 있습니다.",
      "차량 줄의 순서는 ‘차량 순서’ 로 바꿉니다. 저장하면 모든 담당자의 판이 그 순서로 깔립니다.",
      "‘청구처’ 칸이 비어 있으면 그 구간에 매출단가가 둘 이상이라는 뜻입니다. 거기서 골라 두면 나중에 송장을 검증할 때 금액이 바로 붙습니다.",
      "잘못 골랐으면 그 줄의 ‘회차 삭제’ 로 되돌립니다. 차의 배차를 통째로 없애려면 ‘배차 삭제’ 입니다.",
      "상행과 하행 중 한쪽만 넣어도 됩니다. 하행만 깔아 두면 상행은 MES 업로드가 자동으로 채웁니다.",
      "업체 배차는 **구간을 한 번 고르고 업체 줄을** 필요한 만큼 늘립니다. 방향·상차지·하차지는 고른 구간에서 따라오고, 그 구간에 단가가 걸린 품목이 둘 이상이면 줄마다 ‘품목’ 을 고릅니다.",
      "업체 배차 표는 ‘업체별’ 과 ‘구간별’ 로 바꿔 볼 수 있습니다. 기본은 업체별이고, 바꾸신 값은 다음에 열어도 그대로입니다.",
      "업체 배차에도 **구간 색이 따라옵니다** — 기사 배차와 같은 색입니다. 업체별에서는 상차지·하차지 두 칸에, 구간별에서는 구간 이름에 칠합니다. 색은 기준정보 › 구간·장소에서 정합니다.",
    ],
    controls: [
      { name: "남은 줄 등록 (N건)", description: "자동 등록이 실패해 남은 줄을 다시 보냅니다. 구간을 고르면 그 자리에서 등록되므로 **평소에는 지금 화면에 없습니다** — 실패한 줄이 있을 때만 나타납니다." },
      { name: "복사", description: "등록된 줄의 구간·품목·톤수·**청구처**를 복사합니다. 누르면 표 위에 무엇을 복사했는지 뜨고, 같은 방향의 빈 줄마다 ‘붙여넣기’ 가 생깁니다." },
      { name: "차량 복사", description: "그 차량의 **등록된 회차를 전부** 복사합니다 — 경유·하행을 한 번에 옮길 때 씁니다. 차량 묶음의 첫 줄에만 있습니다." },
      { name: "차량 붙여넣기 (N줄)", description: "복사해 둔 회차들을 그 차량의 **빈 줄에만** 넣습니다. 이미 등록된 줄은 덮지 않고 건너뛰며, 접혀 있는 줄(경유·하행 3)은 ‘회차 추가’ 로 먼저 펴야 들어갑니다 — 무엇이 들어가고 무엇이 빠졌는지 알려 줍니다. **‘차량 복사’ 를 누르기 전에는 지금 화면에 없습니다.**" },
      { name: "붙여넣기", description: "복사해 둔 구간을 그 빈 줄에 넣고 **그 자리에서 등록합니다.** **‘복사’ 를 누르기 전에는 지금 화면에 없습니다.**" },
      { name: "복사 지우기", description: "복사해 둔 것을 비웁니다. ‘붙여넣기’ 가 사라집니다. **‘복사’ 를 누르기 전에는 지금 화면에 없습니다.**" },
      { name: "자사 밖 차량", description: "자사 차량이 아닌 차(외부 기사·업체 차량)를 등록하는 칸을 엽니다. 그 차들은 표에 줄이 없습니다." },
      { name: "등록", description: "배차를 만듭니다. 화면은 그대로 있고 목록이 등록한 날짜로 옮겨 가며, 배차번호와 ‘상세’ 링크가 표 위에 남습니다." },
      { name: "상세", description: "그 배차의 운행 회차 관리 화면으로 이동합니다 — 운행 완료·MES 원장 연결은 거기서 합니다. 회차를 늘리는 것은 이 화면의 ‘회차 추가’ 로 끝나므로 그 일로 들어가실 필요는 없습니다. 한 차량에 배차가 둘이면 ‘상세’ 도 둘입니다." },
      { name: "기사", description: "차량에 배정된 기사가 있으면 이름만 보여 줍니다. 배정이 없으면 직접 고를 수 있고, 고르면 그 차량의 배정으로 등록할지 물어봅니다." },
      { name: "자사 기사 N대 / 전체 N대", description: "차량 칸 라벨 옆의 두 칸입니다. 지금 보고 있는 범위가 표시돼 있고, 누르면 그 범위로 바뀝니다. ‘자사 기사’ 는 그 배차일자에 기사가 배정돼 있고 그 기사의 소속이 비어 있거나 자사인 차량입니다. 외주 차량으로 배차할 때만 ‘전체’ 로 넓히세요." },
      { name: "배정 등록", description: "고른 기사를 그 차량의 배정으로 기준정보에 넣습니다. 화면은 그대로 있고 다음 배차부터 차량만 고르면 기사가 따라옵니다." },
      { name: "이번만 쓰기", description: "배정으로 등록하지 않고 이 배차에만 그 기사를 씁니다." },
      { name: "청구처", description: "그 회차를 어디에 청구할지 고릅니다. 구간·품목 열 바로 옆에 있고 ‘자사 밖 차량’ 칸에도 있습니다. 구간에 단가가 걸리는 거래처가 단가와 함께 위로 올라오고, **하나로 정해지면 자동으로 채워집니다** — 보통은 어디로 청구되는지 확인만 하는 칸입니다. **단가가 둘 이상인 구간에는 `고르세요` 가 떠 있으니 직접 고르세요** — 비워 두면 송장을 검증해도 단가를 고를 수 없어 금액이 빕니다. 손대면 그 뒤로는 자동으로 바뀌지 않습니다. 상행도 고르실 수 있습니다 — 상행 단가가 거래처별로 갈린 구간에서 여기 골라 두면 MES 반영이 그 값을 씁니다. 등록되지 않은 빈 줄만 ‘—’ 입니다. **업체 배차 등록 칸도 같습니다** — 구간을 고를 때 그 구간의 청구처가 한 곳뿐이면 줄마다 들어오고, 둘 이상이면 비어 있으니 직접 고르세요. 이미 골라 둔 줄은 구간을 바꿔도 덮지 않습니다. 이 값은 업체 포털에는 보이지 않습니다." },
      { name: "하행 회차 추가", description: "하행 회차를 한 줄 늘립니다. 최대 3건이고, 상행이 없으면 4건까지입니다." },
      { name: "상행 회차 추가", description: "상행 줄을 지웠을 때만 나옵니다. 다시 넣으면 상차지(포스코)·품목(수재)이 채워집니다. 상행은 배차당 1건입니다." },
      { name: "제안", description: "그 구간에 등록한 가톤수를 그 회차의 가실적에 채웁니다. 등록된 값이 없으면 채우지 않습니다." },
      { name: "회차 추가", description: "차량번호 칸 아래에 있습니다. 그 차량에 줄을 하나 늘립니다 — 처음 누르면 상행과 하행 ‘사이에’ 경유(하행 1회차) 줄이, 다시 누르면 맨 아래 ‘하행 3’ 줄이 생깁니다. **누르기만 해서는 아무것도 등록되지 않습니다** — 구간을 골라야 등록됩니다. 이미 등록된 경유·3회차가 있으면 누르지 않아도 그 줄은 보입니다. 줄을 다 편 차량에는 나오지 않습니다 — 상행이 있으면 하행 3까지, 상행이 없으면 하행 4까지입니다." },
      { name: "줄 접기", description: "‘회차 추가’ 로 열어 둔 빈 줄을 다시 감춥니다. **등록된 회차는 이 버튼으로 없어지지 않습니다** — 그때는 ‘회차 삭제’ 입니다. 날짜를 옮겨도 열어 둔 줄은 모두 접힙니다. **빈 줄을 열기 전에는 지금 화면에 없습니다.**" },
      { name: "구간", description: "이 한 칸이 상차지·하차지·품목을 다 정합니다. 구간에 색을 지정해 두면 목록에서도 표에서도 그 색으로 보여 한눈에 갈립니다. 품목·가실적 칸은 고른 구간에서 따라오므로 따로 고르지 않습니다. **상행 줄에서는 하행 구간도 고를 수 있습니다** — 목록 뒤쪽에 ‘하행’ 꼬리표를 달고 나오고, 수재 대신 다른 품목을 싣고 나간 첫 구간을 하행 실적으로 잡을 때 씁니다. 고르면 하행 1회차로 저장되어 경유 줄에 보입니다(경유가 이미 차 있으면 다음 빈 하행 줄로 갑니다). 이미 등록된 상행 회차에서 하행 구간을 골라도 같고, 실적이 확정된 회차는 바꿀 수 없습니다." },
      { name: "업체별 / 구간별", description: "업체 배차 표를 무엇으로 묶어 볼지 고릅니다. ‘업체별’ 은 한 업체에 몇 대를 주고 몇 대가 뛰었는지, ‘구간별’ 은 한 구간을 어느 업체들이 나눠 뛰는지 보여 줍니다. 고르신 값은 이 컴퓨터에 남아 다음에 열어도 그대로입니다. ‘복사’ 는 업체에 보내는 글이라 업체별로 볼 때만 나옵니다." },
      { name: "줄 추가", description: "업체 배차에서 업체 줄을 하나 늘립니다. 한 구간을 여러 업체에 나눠 줄 때 씁니다. 앞줄을 베끼지 않습니다." },
      { name: "구간 빠른 등록", description: "목록에 없는 구간을 그 자리에서 만듭니다. 기준정보로 나갔다 오면 짜던 줄이 날아가기 때문입니다. 장소도 함께 만들 수 있고 단가까지 붙입니다." },
      { name: "차량 순서", description: "기사 배차 표의 차량 줄 순서를 정합니다. 창에서 줄 왼쪽의 손잡이(⋮⋮)를 끌어 옮기거나, 그 옆 칸의 위·아래 화살표를 눌러 한 칸씩 옮기고 ‘저장’ 을 누릅니다. **순서는 서버에 남아 모든 담당자의 화면이 같은 순서로 깔립니다.** ‘차량번호순’ 을 누르면 예전처럼 번호순으로 되돌립니다(저장해야 반영됩니다). 새로 들어온 자사 차량은 맨 아래에 붙습니다." },
      { name: "가톤수(대당)", description: "업체 배차의 **차 한 대당** 톤수입니다. 가실적은 ‘이 값 × 대수’ 로 잡히고, 송장으로 확정된 대수만큼 빠집니다. **구간을 고르면 그 구간에 등록된 가톤수가 칸에 들어옵니다**(기준정보 › 단가 또는 구간·장소에서 넣은 값) — 그 줄만 다르면 고쳐 쓰시면 되고, 비우면 서버가 다시 구간 값을 씁니다. 구간에 가톤수가 없으면 비어 있으니 구간마다 한 번 넣어 두세요." },
      { name: "회차 삭제", description: "그 한 줄만 지웁니다. 같은 배차의 다른 회차는 그대로 남습니다. **확인 창이 그 회차에 무엇이 몇 개 붙어 있는지 세어 적습니다** — `함께 삭제` 는 실적·가실적 근거·매칭 후보, `연결만 해제` 는 송장과 MES 원장입니다(송장 사진은 남고 미매칭으로 되돌아가 다른 회차에 다시 붙일 수 있습니다). 확정된 실적이 있거나 배차의 마지막 한 회차면 `지우기` 가 잠기고 사유가 뜹니다." },
      { name: "배차 삭제", description: "그 배차의 상행·하행 회차를 전부 없앱니다. 되돌릴 수 없습니다 — 한 줄만 잘못 넣으셨으면 ‘회차 삭제’ 를 쓰세요. 운행하지 않기로 한 배차는 지우지 말고 상태를 ‘취소’ 로 두세요." },
      { name: "구간 빠른 등록", description: "목록에 없는 구간을 그 자리에서 만듭니다. 장소·구간·단가를 한 번에 넣고 색도 함께 고릅니다 — 기준정보로 나갔다 오지 않아도 됩니다." },
      { name: "삭제", description: "업체 배차 탭과 등록 칸 안에서 쓰는 이름입니다. 업체 배차 탭에서는 그 지시 줄을 지우고, **확인 창이 무엇이 붙어 있는지 세어 적습니다** — 가실적과 업체가 알려 준 배정 차량은 함께 사라지고, **업체가 올린 송장은 지우지 않고 연결만 끊습니다**(사진은 제출현황·송장 검수에 그대로 남습니다). 사진까지 지우려면 제출현황에서 그 송장을 삭제하세요. 등록 칸에서는 그 줄만 지웁니다." },
      { name: "전일 배차 복사", description: "하루 앞 배차를 그대로 가져옵니다. 두 탭 모두에 있습니다. 기사 배차는 하행 회차만 옵니다 — 상행은 MES 업로드가 채우기 때문입니다. 대상 일자에 그 차량 배차가 이미 있으면 건너뛰므로 두 번 눌러도 늘지 않습니다. 업체 배차는 같은 업체·구간·품목 줄을 건너뜁니다." },
      { name: "배차대수 · 가톤수(대당) · 가실적(톤)", description: "업체에 넘긴 값입니다. 하행이면 이 대수만큼 가실적이 섭니다. **고치는 칸은 ‘가톤수(대당)’** 이고 — 차 한 대당 톤수입니다 — 오른쪽 **‘가실적(톤)’ 은 ‘가톤수 × 배차대수’ 를 보여 주는 읽기 전용 칸**입니다. **소계·총계는 ‘가실적(톤)’ 의 합**이고 ‘가톤수(대당)’ 칸은 비워 둡니다(묶음 안에서 대당이 서로 다를 수 있어 대표값이 없습니다). ‘차이(톤)’ 도 곱한 값으로 셉니다. 가톤수를 비워 두면 구간에 등록한 가톤수를 씁니다." },
      { name: "배정 차량", description: "상행 업체 배차의 이 칸은 눌러서 차량을 넣거나 뺄 수 있습니다 — 업체가 포털을 안 쓰거나 전화로 알려 올 때 담당자가 대신 넣습니다. 그 업체 소속 차량만 고를 수 있고, 목록에 없으면 번호를 직접 적습니다. 점선 밑줄이 그어진 번호는 기준정보에 없는 차량이니 기준정보 · 차량에 등록해 주세요 — 등록 전에는 MES 실적이 그 차에 붙지 않습니다." },
      { name: "작업대수 · 작업톤수", description: "실제로 뛴 값입니다. 상행은 MES 원장 기준이라 실적 확정 전에도 보이고, 하행은 확정된 실적 기준입니다." },
      { name: "차이(대) · 차이(톤)", description: "작업 − 배차. 부호로만 보여 줍니다(+2 · −1). 차이가 없으면 ‘—’ 입니다." },
      { name: "복사", description: "업체 이름 아래에 있습니다. 그 업체 배차만 메신저용 텍스트로 복사합니다 — `[일자] 업체명` 다음에 `방향 상차지 → 하차지 품목 N대 N톤` 이 줄마다 붙습니다. 눌러 두고 카톡에 그대로 붙여 넣으세요." },
      { name: "배차번호", description: "끝 세 자리만 보여 줍니다 — 하루치만 보는 화면이라 앞의 날짜는 모든 줄이 같습니다. 칸에 마우스를 올리면 `DSP-20260915-001` 처럼 전체 번호가 뜹니다." },
      { name: "엑셀 다운로드 · 인쇄", description: "일자 이동 줄 오른쪽에 있습니다. 인쇄는 열려 있는 등록 칸을 먼저 접고 표만 찍습니다." },
    ],
    notes: [
      INLINE_EDIT,
      DROPDOWN_SEARCH,
      "품목·상차지·하차지 열은 없습니다. 구간이 곧 그 셋의 조합이라 구간 한 칸에서 정합니다 — 셋을 따로 고르면 서로 어긋난 회차가 만들어집니다.",
      "‘경유’·‘하행’·‘하행 3’ 은 모두 하행 회차입니다 — 경유가 1회차, 하행이 2회차, ‘하행 3’ 이 3회차, ‘하행 4’ 가 4회차로 저장됩니다. 한 구간만 뛰는 날은 예전 배차표처럼 ‘하행’ 줄에 적으시면 됩니다.",
      "실적의 상행은 수재만입니다. 수재 대신 무연탄 등을 싣고 나간 날은 상행 줄에서 하행 구간을 고르시면 하행(기타) 실적으로 잡힙니다. 그 구간이 기준정보에 상행으로 등록돼 있으면 기준정보 · 구간에서 방향을 하행으로 바꾼 뒤 다시 고르세요.",
      "기본으로 깔리는 줄은 상행·하행 둘입니다. 자사 11대가 스크롤 없이 한 화면에 들어오도록 나머지는 접어 두었고, 필요한 차량만 ‘회차 추가’ 로 늘립니다. 한 화면에 다 넣으시려면 오른쪽 위 표 밀도를 ‘조밀’ 로 두세요.",
      "표의 톤수 단위는 톤입니다.",
      "송장은 하행 회차마다 상차 1장·하차 1장이 필요하며 상행에는 없습니다.",
      "검색·차량·기사·업체·상태 중 하나라도 걸면 빈 줄을 깔지 않고 조회 결과만 보여 줍니다. 등록하시려면 필터를 비우세요.",
      "표에 줄이 없으면 기준정보 · 차량·기사에서 그 차량을 ‘자사 차량’ 으로 지정해 주세요.",
      "배차 1건은 상행 1회차와 하행 1~3회차로 이루어집니다. 상행이 없는 날은 하행을 4회차까지 넣을 수 있습니다. 한쪽만 있어도 되지만 회차가 0건인 배차는 없습니다.",
      "같은 차량에 배차가 둘이면(같은 차가 하루에 두 번 뛰거나 MES 업로드가 자동으로 만들어) 표에서 붙어 나오고 차량·기사·업체 칸이 한 번만 열립니다. ‘배차번호’·‘상태’·‘상세’·‘삭제’ 는 배차마다 따로 있습니다.",
      "상행 상차지·품목은 포스코·수재가 미리 골라져 있고, 눌러서 다른 값으로 바꿀 수 있습니다 — 포스코 출입 정지나 수재 외 품목을 나르는 드문 경우를 위한 것입니다.",
      "상행 하차지는 MES 상행 도착지로 표시된 곳만 보여 줍니다. 표시는 기준정보 › 장소에서 켜고 끕니다. 비워 두면 첫 하행의 상차지를 씁니다.",
      "하행 상차지·하차지는 한쪽을 고르면 구간이 등록된 상대가 ‘구간 있음’ 꼬리표와 함께 위로 올라옵니다. 목록에서 빠지지는 않으므로 계약에 없는 구간으로도 배차할 수 있습니다.",
      "같은 차량·일자에 이미 배차가 있으면 확인 창이 뜹니다. 같은 차가 하루에 두 번 뛰는 것처럼 정상적인 중복이면 ‘하루 두 번 운행으로 등록’ 을 누릅니다.",
      "‘삭제’ 는 잘못 등록한 배차를 없앨 때만 씁니다. 운행하지 않기로 한 배차는 지우지 말고 상태를 ‘취소’ 로 두세요 — 취소는 업무 기록으로 남고 이미 만들어진 실적을 건드리지 않습니다.",
      "삭제하면 그 배차의 송장(이미지 파일 포함)과 실적이 함께 사라집니다. 결산 명세에 이미 반영된 실적이 있으면 삭제할 수 없습니다.",
      "업체 배차는 차량 단위로 짜지 않습니다. 어느 차가 뛸지는 업체가 정하므로 구간·품목·대수만 넘깁니다.",
      "업체 배차가 없어도 실적은 잡힙니다. 메모 칸이 ‘배차 없음’ 인 줄은 MES·송장에서 온 실적입니다.",
      "하행 업체 배차는 가실적의 근거입니다. 하행은 실톤수를 매번 기록하기 어려워 가톤수로 잡고, 업체 송장이 검증되면 그만큼 가실적이 줄어듭니다.",
      "같은 업체·구간·품목 배차를 오전·오후로 나눠 넣을 수 있습니다. 그때는 실적이 첫 줄에만 붙고 표 위에 알림이 뜹니다.",
      "상행 업체 배차는 대조용입니다. 상행 실적은 MES 업로드가 만듭니다.",
    ],
    related: [L.dispatch, L.vehicle, L.location, L.item, L.company, L.performance],
  },
  {
    pattern: "/admin/users",
    title: "사용자 관리",
    screenshot: "/help/users.png",
    summary:
      "담당자·시스템관리자와 포털 계정(운송업체·청구업체)을 만들고 역할·상태를 관리합니다. 시스템관리자만 들어올 수 있습니다.",
    steps: [
      "오른쪽 위 ‘사용자 추가’ 를 누릅니다.",
      "아이디·이름·초기 비밀번호(8자 이상)를 넣고 역할을 고릅니다.",
      "역할을 ‘운송업체’ 나 ‘청구업체’ 로 고르면 소속 업체 칸이 나옵니다. 반드시 고르세요 — 소속이 그 계정이 보는 범위입니다.",
      "표의 이름·역할·연락처는 눌러서 바로 고칩니다.",
    ],
    controls: [
      { name: "사용자 추가", description: "새 계정 줄을 엽니다. 이 화면의 주 동작입니다." },
      { name: "역할", description: "`담당자` · `시스템관리자` · `운송업체` · `청구업체` 넷입니다. 포털 계정이 **두 기능을 함께** 쓰는 설정은 기준정보 › 거래처의 ‘계정’ 에서 합니다. **포털 역할 둘은 관리자 화면에 들어오지 못하고** 각자의 포털만 씁니다 — 운송업체는 받은 배차를 보고 송장을 올리고(`/carrier`), 청구업체는 자기 청구분의 운송장 사진을 받아 갑니다(`/client`). 저장되는 값은 `업체`·`청구업체` 입니다." },
      { name: "소속 업체", description: "포털 계정이 볼 수 있는 범위입니다. **고를 수 있는 거래처가 역할에 따라 다릅니다** — 운송업체는 협력 운송사, 청구업체는 화주·청구처입니다. 이 값이 곧 그 계정이 보는 배차·실적·사진의 범위라 반드시 맞아야 합니다." },
      { name: "비밀번호 초기화", description: "새 비밀번호를 지정합니다. 8자 이상입니다." },
      { name: "비활성화", description: "계정을 잠급니다. 로그인만 막고 이력은 그대로 둡니다. 본인 계정과 마지막 활성 시스템관리자는 잠글 수 없습니다." },
      { name: "삭제", description: "**한 번도 쓰지 않은 계정만** 지울 수 있습니다. 배차·송장·실적·정산에 걸려 있으면 어디서 몇 건 쓰는지 알려드리고 지워지지 않습니다 — 그때는 ‘비활성화’ 입니다. 지운 계정이 한 일은 시스템 · 변경 이력에 그대로 남습니다." },
    ],
    notes: [
      INLINE_EDIT,
      "기사 계정은 여기가 아니라 기사관리 · 기사 계정 화면에서 관리합니다.",
      "포털 계정은 외부에 주는 열쇠입니다. 담당자도 발급할 수 있지만, 그 길(기준정보 · 거래처)로는 포털 계정만 만들 수 있습니다 — 담당자·시스템관리자 계정은 이 화면에서만 다룹니다.",
      "포털 계정에 소속을 지정하지 않으면 저장되지 않습니다 — 소속이 없으면 범위를 좁힐 축이 없어 전체가 보이게 되기 때문입니다. 표에서 역할만 포털 역할로 바꾸는 것도 같은 이유로 막힙니다.",
      "들어가는 주소가 역할마다 다릅니다 — 운송업체는 `/carrier/login`, 청구업체는 `/client/login` 입니다. 담당자 로그인 화면과 다릅니다.",
    ],
    related: [L.company, L.auditLogs],
  },
  {
    pattern: "/admin/master/company",
    title: "거래처",
    screenshot: "/help/master-company.png",
    summary:
      "화주·청구처·상품소유업체·협력 운송사 역할을 중복 지정할 수 있습니다. 같은 회사가 화주이면서 운송사인 경우가 실제로 존재합니다.",
    steps: [
      "오른쪽 위 ‘신규 등록’ 을 누르거나, 표의 값을 눌러 그 자리에서 고칩니다.",
      "포털 계정을 주려면 그 줄 ‘계정’ 을 누릅니다. 패널에서 아이디·이름·초기 비밀번호를 넣고 **쓸 수 있는 기능**(운송업체·청구업체)을 체크합니다 — 둘 다인 거래처는 **계정 하나에 둘을 함께** 켜 주세요.",
      "쓰지 않는 거래처는 ‘사용’ 칸을 미사용으로 바꿉니다. 배차 드롭다운에서 사라집니다.",
    ],
    controls: [
      { name: "거래처코드", description: "목록은 이 코드의 번호순입니다. ‘신규 등록’ 을 누르면 다음 번호가 들어와 있고 고쳐 쓸 수 있습니다." },
      { name: "주소 · 업태 · 종목", description: "거래명세서의 공급자·공급받는자 칸에 그대로 찍힙니다. 개인 차주 명세서는 기사관리 › 기사 계정의 ‘명세서 거래처’ 로 고른 거래처의 값이 나갑니다." },
      { name: "계정", description: "그 거래처의 포털 계정을 만들고 비밀번호를 바꾸고 잠급니다. 담당자도 발급할 수 있고, 이 버튼으로는 포털 계정만 다루므로 담당자·시스템관리자 계정은 여기서 만들 수도 볼 수도 없습니다. 운송사이면서 화주인 거래처도 **계정은 하나**입니다 — 패널 안에서 기능을 둘 다 켜 주세요." },
      { name: "쓸 수 있는 기능", description: "그 계정이 들어올 수 있는 포털입니다. ‘운송업체’ 는 받은 배차를 보고 송장을 올리는 포털(`/carrier`), ‘청구업체’ 는 자기 청구분의 운송장 사진을 받아 가는 포털(`/client`) 입니다. 체크를 바꾸면 그 계정이 **다시 로그인했을 때** 메뉴가 바뀝니다. 하나는 켜 두어야 합니다 — 쓰지 않을 계정은 ‘잠그기’ 로 막으세요." },
      { name: "비밀번호 변경 · 잠그기 · 삭제", description: "포털 계정 칸 안에 있습니다. ‘잠그기’ 는 로그인만 막고 계정은 남깁니다. ‘삭제’ 는 **한 번도 쓰지 않은 계정만** 지웁니다 — 발급만 해 두고 아이디를 다시 정하실 때 쓰세요. 지우기 전에 한 번 묻고, 배차·송장·실적에 걸려 있으면 어디서 몇 건 쓰는지 알려 주고 지우지 않습니다. 지운 아이디는 다시 쓸 수 있습니다." },
      { name: "화주 · 상품소유 · 운송사", description: "역할 플래그입니다. 중복 지정할 수 있습니다." },
      { name: "삭제", description: "**어디서도 쓰지 않는 거래처만** 지웁니다. 배차·실적·단가·명세서 어디에든 걸려 있으면 어디서 몇 건 쓰는지 알려 주고 지우지 않습니다 — 그때는 ‘사용’ 을 미사용으로 바꾸세요. 테스트로 만들어 본 줄을 치우는 용도입니다." },
      { name: "신규 등록", description: "표 맨 위에 입력 줄을 엽니다. 다시 누르면 ‘신규 등록 취소’ 로 바뀌어 줄을 접습니다." },
      { name: "사용으로 / 미사용으로", description: "고른 행의 사용 여부를 한 번에 바꿉니다. 표 맨 앞 네모로 고르고, 머리글 네모는 보고 있는 페이지를 통째로 집습니다." },
      { name: "전체 N곳 선택", description: "표 위 조회조건에 **지금 걸어 두신 그 조건**에 맞는 전부를 집습니다 — 보고 있는 페이지가 아닙니다(그건 머리글 네모입니다). 걸린 조건은 표 바로 위 꼬리표로 보이고 숫자는 그 조건으로 조회된 건수와 같습니다. 아무것도 안 걸면 말 그대로 전부입니다. 페이지를 넘겨 가며 집지 않아도 됩니다." },
    ],
    notes: [
      INLINE_EDIT,
      "**미사용으로 바꾸면 배차 화면의 업체·상품소유업체·청구처 드롭다운에서 빠집니다.** 목록을 줄이는 정식 방법입니다. 이미 그 업체로 짜 둔 배차에서는 값이 그대로 보입니다.",
      "**같은 사업자등록번호는 두 번 등록되지 않습니다.** 하이픈을 빼고 넣어도 같은 번호로 봅니다. 번호를 비워 두는 것은 얼마든지 됩니다.",
      "초기 비밀번호는 담당자가 정해 거래처에 알려 줍니다. 화면에 가리지 않고 보여 주는 이유도 그것입니다 — 가려 두면 옮겨 적을 수 없습니다.",
      "거래처는 로그인한 뒤 ‘내 정보’ 에서 스스로 비밀번호를 바꿀 수 있습니다. 들어가는 주소는 운송업체가 `/carrier/login`, 청구업체가 `/client/login` 인데 **로그인 처리는 같아서** 어느 쪽으로 쳐도 들어옵니다 — 두 기능을 켠 계정은 들어온 뒤 탭에 양쪽 메뉴가 함께 보입니다.",
      "**청구업체 계정을 만드는 것이 곧 ‘사진 보낼 업체’ 설정입니다.** 계정이 있는 거래처만 자기 청구분의 운송장 사진을 봅니다. 보내지 않기로 했으면 계정을 잠그세요.",
      "계정을 잠그면 그 거래처는 포털에 들어오지 못합니다. **한 번도 쓰지 않은 계정은 지울 수도 있습니다** — 발급만 해 두고 아이디를 다시 정하실 때 쓰세요. 배차·송장·실적에 걸려 있으면 지워지지 않고 어디서 몇 건 쓰는지 알려드립니다(그때는 잠그기). 지운 계정이 한 일은 변경 이력에 그대로 남습니다.",
    ],
    related: [L.dispatch],
  },
  {
    pattern: "/admin/dispatch/:id/edit",
    title: "배차 수정",
    screenshot: "/help/dispatch-edit.png",
    summary:
      "이미 만든 배차의 머리 정보(일자·차량·상태·특이사항)를 고치는 화면입니다. 회차별 내용은 운행 회차 관리에서 다룹니다.",
    steps: [
      "표에서 고칠 값을 바로 누르고 고칩니다. 고치면 곧바로 저장됩니다.",
      "다 고쳤으면 ‘기사 배차로’ 를 눌러 목록으로 돌아갑니다.",
    ],
    controls: [
      { name: "기사 배차로", description: "배차 등록 화면의 기사 배차 탭으로 돌아갑니다." },
    ],
    notes: [
      INLINE_EDIT,
      "차량을 바꾸면 그 일자의 배정에 맞춰 기사·업체가 다시 채워집니다.",
    ],
    related: [L.vehicle, L.dispatch],
  },
  {
    pattern: "/admin/dispatch/:id/operation",
    title: "운행 회차 관리",
    screenshot: "/help/dispatch-operation.png",
    summary:
      "배차 1건에 달린 상행·하행 회차를 보고 고치는 화면입니다. 회차를 더하거나 지우고, 실제 운행이 끝난 회차를 완료로 넘깁니다.",
    steps: [
      "표에서 회차별 상차지·하차지·품목·톤수를 확인합니다.",
      "고칠 값은 바로 누르고 고칩니다.",
      "운행이 끝난 회차는 ‘운행 완료’ 로 넘깁니다.",
      "상행 실적이 비어 있으면 맨 아래 ‘붙일 수 있는 MES 실적 찾기’ 로 올라와 있는 상행실적을 잇습니다.",
      "다 봤으면 ‘기사 배차로’ 로 돌아갑니다.",
    ],
    controls: [
      { name: "운행 완료", description: "그 회차를 완료 상태로 바꿉니다." },
      { name: "저장", description: "새로 추가하는 회차 줄을 저장합니다." },
      { name: "삭제", description: "그 회차를 지웁니다." },
      { name: "붙일 수 있는 MES 실적 찾기", description: "이 배차의 운송일자·차량과 맞는 미매칭 MES 상행실적을 찾습니다." },
      { name: "이 회차에 연결", description: "고른 MES 상행실적을 이 배차의 상행 회차에 붙입니다." },
      { name: "기사 배차로", description: "배차 등록 화면의 기사 배차 탭으로 돌아갑니다." },
    ],
    notes: [
      INLINE_EDIT,
      "하행 회차마다 상차 송장 1장과 하차 송장 1장이 붙습니다. 상행에는 송장이 없습니다.",
      "하행 건수는 상행 건수 이상이어야 합니다.",
      "상행에는 송장이 없어 MES 업로드가 유일한 확정 근거입니다. MES 화면에서 배차를 찾는 것과 여기서 실적을 찾는 것은 같은 일을 양쪽에서 하는 것이니 편한 쪽을 쓰면 됩니다.",
    ],
    related: [L.invoice, L.route, L.item, { label: "MES 상행실적 업로드", path: "/admin/performance/mes-upload", reason: "상행 실적의 원본과 매칭 상태를 봅니다" }],
  },
];

// ---------------------------------------------------------------------------
// 일일실적
// ---------------------------------------------------------------------------

const PERFORMANCE: HelpTopic[] = [
  {
    pattern: "/admin/performance",
    title: "일일실적 현황",
    screenshot: "/help/performance.png",
    summary:
      "하루치 실적을 상행/하행 > 품목 > 구간 계층으로 묶어 보는 집계표입니다. 구간마다 물량 줄과 금액 줄 두 줄로 그립니다.",
    steps: [
      "날짜 이동으로 볼 날짜를 고릅니다.",
      "‘가실적’ 탭에서 아직 확정되지 않은 값을, ‘확정’ 탭에서 확정된 값을 봅니다.",
      "확인이 끝난 줄은 ‘확정’ 으로 넘깁니다. 하루치를 한 번에 넘기려면 ‘이 일자 전체 확정’ 을 씁니다.",
      "**단가나 청구처가 없는 건은 확정되지 않고 가실적으로 남습니다.** 금액이 빈 채로 확정되면 결산에서야 걸려 월말에 몰리기 때문입니다. 결과 상자가 몇 건이 왜 남았는지 알려 주니, 채운 뒤 다시 확정하세요.",
    ],
    controls: [
      { name: "가실적 / 확정", description: "확정 전 값과 확정된 값을 나눠 보는 탭입니다." },
      { name: "확정 취소", description: "확정을 되돌립니다." },
      { name: "확정", description: "그 줄의 가실적을 확정합니다. **단가·청구처가 없으면 확정되지 않고 가실적으로 남습니다** — 결과 상자가 몇 건이 확정됐고 몇 건이 왜 남았는지 알려 줍니다. 청구처는 `배차 등록` 이나 `송장 검수` 에서, 단가는 `기준정보 › 단가` 에서 채운 뒤 다시 누르세요." },
      { name: "이 일자 전체 확정", description: "그 날짜의 가실적을 한꺼번에 확정합니다. 화면에 건 **품목·방향 필터가 함께 적용**되므로 표에 보이는 것만 확정됩니다. 단가·청구처가 없는 건은 가실적으로 남고 사유가 함께 뜹니다." },
      { name: "단가 재적용", description: "조회 중인 날짜의 실적에 최신 단가를 다시 적용합니다. 결산이 확정된 실적은 건드리지 않습니다." },
      { name: "엑셀(CSV) 다운로드", description: "지금 보이는 집계표를 CSV 로 내려받습니다." },
      { name: "인쇄", description: "브라우저 인쇄 창을 엽니다." },
    ],
    notes: [
      "계획 열은 그 달의 월간 계획에서 옵니다. 계획이 비어 있으면 기준정보 · 월간 계획에 먼저 넣으세요.",
      "배차를 짜면 상행·하행 모두 그 자리에서 가실적으로 잡힙니다. 상행은 MES 업로드로, 하행은 송장 검증으로 확정되고 그때 같은 줄이 확정 톤수로 바뀝니다.",
      "머리말의 ‘당일 상행 N톤’ 에 ‘가실적 포함 — MES 반영 전’ 이 붙어 있으면 아직 확정 전입니다. MES 파일 톤수와 맞춰 보실 때는 반영 뒤에 보세요.",
      "상행 가톤수는 배차에 적은 값, 없으면 구간에 등록한 가톤수입니다. 평균을 내어 채우지 않습니다. 둘 다 비어 있으면 0으로 보이니 기준정보 · 구간·장소에서 상행 구간의 가톤수를 넣어 두세요.",
      "금액은 매출·매입 단가에서 계산됩니다. 톤수는 같고 단가만 다릅니다.",
      "**금액의 원 단위는 구간마다 톤수 합 × 단가에서 소수점을 버린 값입니다**(엑셀 일일실적의 INT 와 같습니다). 그래서 ‘건별 실적’ 금액을 더한 값과 몇 원 다를 수 있습니다. ‘금일 누계’ 는 엑셀처럼 ‘전일 누계’ 와 ‘일일작업’ 을 따로 버려 더하므로 ‘실적(B)’ 과 구간마다 1원 다를 수 있습니다.",
      "**하행은 소계 분류(클링커 · 석회석 · 기타 …)로 나눠 소계를 냅니다** — ‘클링커 소계’·‘석회석 소계’·‘기타 소계’. 상행은 소계 없이 상행 계로 봅니다(CSV 에는 상행 소계도 들어 있습니다). 분류는 기준정보 › 품목 › ‘소계 분류’ 탭에서 더하고 고칩니다.",
      "어느 소계에 들어갈지는 **월말 실적과 같은 규칙**입니다 — 그 달만 지정 → 구간·품목 고정 → 품목의 ‘소계 분류’ → 기본 분류(기타) 순서입니다. 바꾸는 곳은 ‘월말 실적’ 의 ‘분류 편집’ 이고 여기에도 바로 반영됩니다. ‘이번 달만’ 으로 잡은 줄은 구간 옆에 흐리게 적힙니다. 단가·금액은 그대로입니다.",
      "상행 계·하행 계는 연녹색, 상·하행 총계는 노란색으로 칠해 둡니다.",
      "**표 아래 진도율은 엑셀 세부내역 하단과 같은 네 줄입니다** — 금액 상행·금액 하행·금액 전체·물량 전체. 실적(B) ÷ (계획(A) ÷ 작업일수 × 경과일수)이라, 지난 날짜만큼 계획을 나눠 놓고 얼마나 따라왔는지를 봅니다. 100% 면 계획대로입니다.",
      "일일계획(C)·계획대비(D-C)·남은일 계획량의 물량은 소수 둘째 자리로 반올림합니다. 다른 물량 칸은 셋째 자리까지입니다.",
      "같은 품목 안에서는 **월간 계획(A)이 적힌 구간이 먼저** 섭니다. 나머지는 하차지 순서입니다. 월말 실적도 같은 순서입니다.",
      "아래 ‘건별 실적’ 의 ‘가실적(톤)’ 은 배차를 등록하면 저절로 채워집니다 — 그 회차에 적은 가실적, 없으면 구간에 등록된 가톤수, 그것도 없으면 29톤입니다. 여기서 고치면 그 값이 가장 셉니다.",
      "**하행은 송장이 검증돼도 이 화면에서는 가톤수로 집계됩니다** (고객 요청 2026-10-14 — 아침에 가장 먼저 보는 화면이고 대략적인 매출을 읽는 자리입니다). 하행 물량은 **배차(기사·업체)에 적은 가톤수 → 구간에 등록한 가톤수 × 횟수 → 29톤 × 횟수** 순이고 금액은 물량 × 단가입니다. 확정 톤수로 보시려면 ‘월말 실적’ 또는 결산업무 · 매출·매입 집계표를 보세요 — 그래서 이 화면의 하행 금액은 결산과 다를 수 있습니다.",
      "상행은 MES 업로드가 넣은 **실제 톤수** 그대로입니다. 방향마다 기준이 다릅니다.",
    ],
    related: [L.plan, L.price, L.dispatch, L.invoice],
  },
  {
    pattern: "/admin/performance/monthly",
    title: "월말 실적",
    screenshot: "/help/performance-monthly.png",
    summary:
      "월말 마감 뒤의 확정 실적입니다. 일일실적 현황과 같은 표를 한 달치로 보며, 월말 톤수 조정까지 얹혀 있습니다.",
    steps: [
      "월 이동으로 볼 달을 고릅니다. 기본은 지난달입니다.",
      "상행 계·하행 계·총계로 그 달 전체를 확인합니다.",
      "숫자가 결산과 맞는지 보려면 결산업무 · 매출·매입 집계표와 나란히 두고 대조하세요 — 같은 기준이라 맞아야 합니다.",
    ],
    controls: [
      { name: "전월 / 다음월", description: "한 달씩 앞뒤로 이동합니다." },
      { name: "가실적 포함", description: "아직 확정되지 않은 가실적까지 더해 봅니다. 기본은 확정 실적만입니다 — 월말 마감 결과를 보는 화면이기 때문입니다." },
      { name: "분류 편집", description: "하행 구간을 어느 소계(클링커 · 석회석 · 기타 …)에 넣을지 바꿉니다(고객 요청 2026-10-19). 켜면 하행 구간 줄을 **다른 분류 위로 끌어다 놓거나**, 구간 오른쪽 드롭다운에서 고릅니다. 다 바꿨으면 ‘분류 편집 끝내기’ 를 누르세요." },
      { name: "고정 / 이번 달만", description: "분류 편집을 켜면 **하행 구간 줄마다** 분류 드롭다운 아래에 나옵니다. 그 줄을 끌어다 놓거나 드롭다운으로 바꿀 때 이 값으로 저장합니다. **‘고정’(기본)** 은 그 구간·품목에 계속 적용되어 다음 달에도 이어지고, **‘이번 달만’** 은 보고 있는 달에만 적용되어 다음 달은 고정값으로 돌아갑니다 — 실적 때문에 한 달만 다른 소계로 잡을 때 씁니다. ‘이번 달만’ 으로 잡아 둔 줄에서 ‘고정’ 을 누르면 지금 분류가 그대로 고정값이 됩니다. 원래 분류를 고르면 그 지정이 지워집니다." },
      { name: "인쇄", description: "브라우저 인쇄 창을 엽니다." },
    ],
    notes: [
      "**일일실적 현황과 숫자가 다를 수 있습니다.** 일일실적은 하행을 가톤수로 집계하지만 여기는 확정 톤수를 씁니다. 여기 숫자가 매출·매입 집계표·거래명세서와 같은 기준입니다.",
      "월말 톤수 조정(제품 수분 감량)이 얹혀 있습니다. 걸린 달에는 머리말에 ‘조정 N톤’ 이 함께 뜹니다 — 조정은 원장을 건드리지 않고 집계에만 얹힙니다.",
      "계획 열은 그 달의 월간 계획에서 옵니다. 계획이 비어 있으면 달성율이 빈칸입니다.",
      "일일계획·일일작업처럼 하루 단위 지표는 없습니다. 한 달치를 보는 표라 뜻이 없기 때문입니다.",
      "상행 계·하행 계는 연녹색, 상·하행 총계는 노란색으로 칠해 둡니다 — 일일실적과 같습니다.",
      "**하행은 소계 분류(클링커 · 석회석 · 기타 …)로 나눠 소계를 냅니다.** 분류 이름은 칸으로 따로 두지 않고 소계 줄이 말합니다. 소계 줄 배경색은 기준정보 › 품목 › ‘소계 분류’ 탭에서 분류마다 정합니다. 품목 칸은 그대로 품목입니다. 분류를 더하거나 이름·순서를 바꾸는 것은 기준정보 › 품목 › ‘소계 분류’ 탭입니다.",
      "어느 소계에 들어갈지는 **그 달만 지정 → 구간·품목 고정 → 품목의 ‘소계 분류’ → 기본 분류(기타)** 순서로 정해집니다. 품목의 분류는 기준정보 › 품목의 ‘소계 분류’ 칸이고 지금은 ‘클링커’ 만 클링커이고 나머지는 기타입니다 — 이름에 석회석이 들어가도 기타입니다(고객 설명). 포스코로 들어가는 석회석소괴·석회석분광(영월청림자원·제천대성MDI·삼성석회)과 백운석분광(단양(석교)대성MDI)은 석회석으로 고정해 두었습니다.",
      "일 단위로는 나누지 않습니다 — 그 달 물량이 통째로 옮겨 갑니다. 분류는 **단가를 바꾸지 않습니다.** 일일실적 현황과 매출·매입 집계표(분류별 보기)가 같은 분류를 따라갑니다.",
    ],
    related: [
      L.plan,
      { label: "일일실적 현황", path: "/admin/performance", reason: "하루치를 가톤수 기준으로 봅니다" },
      { label: "매출·매입 집계표", path: "/admin/settlement/summary", reason: "같은 숫자를 업체별로 봅니다" },
    ],
  },
  {
    pattern: "/admin/performance/mes-upload",
    title: "MES 상행실적 업로드",
    screenshot: "/help/performance-mes-upload.png",
    summary:
      "파일을 올리면 **수재 대상 행 선별부터 배차 매칭, 상행 실적 확정까지 자동으로 끝납니다.** 표에서 배차가 안 붙은 행만 손보면 됩니다. 지난 파일도 위 ‘업로드’ 에서 골라 같은 화면으로 봅니다.",
    steps: [
      "‘새 파일 업로드’ 로 MES 엑셀을 올리거나, 위 ‘업로드’ 에서 지난 파일을 고릅니다.",
      "올리는 즉시 수재 대상 행을 골라내고, 배차를 찾고, 상행 실적까지 확정합니다. 따로 누를 버튼이 없습니다.",
      "컬럼 매핑은 엑셀 헤더와 다 맞으면 한 줄로 접혀 나옵니다. 확인할 필드가 있을 때만 펼쳐집니다.",
      "‘해석하지 못한 목적지’ 줄에서 몇 종·몇 행인지 바로 보입니다. 손볼 것이 있으면 ‘펼쳐서 별칭 등록’ 을 누릅니다.",
      "표의 ‘붙은 배차’ 칸을 봅니다. 안 붙은 행은 그 자리에서 ‘배차 고르기’ 를 누르면 **그 행 아래로 후보가 펼쳐집니다.**",
      "맞는 배차가 없으면 후보 아래 ‘배차 등록하러 가기’ 로 넘어갑니다. 등록하면 이 화면으로 돌아와 자동으로 다시 맞춥니다.",
      "목록은 표 안에서 내리면 계속 이어집니다. 머리글은 고정되고 페이지를 넘길 필요가 없습니다.",
      "기본은 ‘대상’ 행만 봅니다. 대상 외·미해석·소계까지 보려면 ‘선별’ 을 비웁니다.",
      "며칠치를 한 파일로 올렸으면 ‘운송일자’ 에서 하루를 골라 그 날짜만 봅니다. 업체별 집계도 함께 그 날짜로 좁혀집니다.",
      "미매칭 행을 손본 뒤에는 표 오른쪽 위 ‘상행 실적 확정’ 을 한 번 더 눌러 그 행까지 실적으로 만듭니다.",
    ],
    controls: [
      { name: "업로드", description: "지난 업로드를 골라 엽니다. 파일명·일시·상태로 검색되고, 고르면 새로 올린 것과 같은 화면이 나옵니다." },
      { name: "새 파일 업로드", description: "MES 엑셀 파일을 고릅니다. 파일을 끌어다 놓아도 됩니다. 올리는 즉시 대상 선별과 배차 매칭이 끝납니다." },
      { name: "전체 보기 / 접기", description: "컬럼 매핑을 펼치거나 접습니다. 헤더와 다 맞으면 접힌 채로 나옵니다." },
      { name: "매핑 적용·재검증", description: "고친 컬럼 매핑으로 파일을 다시 읽고, 대상 행을 다시 골라냅니다." },
      { name: "펼쳐서 별칭 등록", description: "해석하지 못한 목적지 목록을 폅니다. 접혀 있어도 종수·행수와 표기 두 개는 보입니다." },
      { name: "별칭 등록", description: "미해석 목적지를 표준 장소의 별칭으로 그 자리에서 잇습니다. 출처는 MES 로 등록되고, 잇는 즉시 선별이 다시 계산됩니다." },
      { name: "운송일자", description: "파일에 담긴 날짜만 골라 그 날짜 행과 업체별 집계를 봅니다. 비우면 파일 전체입니다. **보기만 바뀌고** 재매칭·확정·버리기는 파일 전체에 그대로 적용됩니다." },
      { name: "선별", description: "표에서 볼 행을 고릅니다. 기본은 ‘대상’ 이고, 비우면 대상 외·미해석·소계까지 전부 봅니다." },
      { name: "배차 매칭", description: "매칭 상태로 거릅니다. 이미 다른 파일이 가져간 중복행은 기본으로 빠져 있고, 여기서 ‘중복’ 을 고르면 나옵니다." },
      { name: "행 상태", description: "파일을 읽은 결과(정상·오류·중복)로 거릅니다." },
      { name: "배차 고르기", description: "붙은 배차 칸에서 바로 누릅니다. 그 행 아래로 후보가 펼쳐집니다 — 상행 실적이 아직 없는 배차만 나옵니다." },
      { name: "바꾸기", description: "이미 붙은 배차를 다른 회차로 바꿉니다." },
      { name: "이 배차에 연결", description: "고른 회차에 그 행을 붙입니다. 감사 로그에 어떤 행이 어느 회차에 붙었는지 남습니다." },
      { name: "배차 등록하러 가기", description: "맞는 배차가 없을 때 배차 등록 화면으로 갑니다. 운송일자·차량·상행 도착지가 미리 채워지고, 등록하면 이 화면으로 돌아와 자동으로 다시 맞춥니다." },
      { name: "미매칭 배차 다시 찾기", description: "배차나 기준정보를 고친 뒤 아직 붙지 않은 행을 다시 맞춥니다. **재업로드 대신 이걸 씁니다.**" },
      { name: "파일 다시 반영", description: "컬럼 매핑을 고친 뒤 대상 행을 처음부터 다시 골라내고 실적도 다시 확정합니다. 손으로 이어 둔 배차는 그대로 남습니다. **결산을 확정한 뒤에는 막힙니다.**" },
      { name: "확정 취소", description: "확정한 상행 실적을 가실적으로 되돌립니다. 사유가 필수이고 감사 로그에 남습니다." },
      { name: "상행 실적 확정", description: "반영이 이미 확정해 두므로 평소에는 누를 일이 없습니다. **미매칭 행을 손으로 이어 준 뒤** 그 행까지 실적으로 만들 때 누릅니다. 누르면 표 아래 결과로 화면이 내려갑니다." },
      { name: "… · 자세히", description: "방금 한 재매칭·확정 결과를 버튼 옆에 한 줄로 보여 줍니다. 누르면 표 아래 자세한 결과로 내려갑니다." },
      { name: "오류행 엑셀", description: "읽지 못한 행만 따로 내려받아 원본을 고칠 때 씁니다." },
      { name: "이 업로드 버리기", description: "결산 확정 전이면 반영된 업로드도 통째로 버릴 수 있습니다. 그 실적의 확정도 함께 풀립니다." },
    ],
    notes: [
      "**배차를 뒤늦게 등록했다면 재업로드하지 마세요.** 같은 파일을 다시 올리면 송장번호가 겹쳐 전부 중복으로 막힙니다. 위 ‘업로드’ 에서 그 파일을 골라 ‘미매칭 배차 다시 찾기’ 를 누르면 재업로드 없이 다시 맞춥니다.",
      "**결산을 확정하기 전까지는 되돌릴 수 있습니다.** 매핑을 고쳐 ‘파일 다시 반영’ 할 수도, 업로드를 통째로 버릴 수도 있습니다. 그래서 올릴 때 확인을 받지 않고 바로 처리합니다. 결산까지 확정한 뒤에는 둘 다 막히니 결산 확정을 먼저 취소하세요.",
      "**운송일자는 상차일시 기준으로 07시부터 다음날 07시까지를 당일로 봅니다.** 10/4 새벽 5시에 상차한 건은 10/3 실적입니다. 그래서 표의 ‘상차일시’ 와 ‘운송일자’ 가 다른 행이 있고, 그것이 정상입니다 — 하루치 파일도 달력으로는 이틀에 걸쳐 있습니다.",
      "**주말·연휴 뒤에 며칠치를 한 파일로 올려도 됩니다.** 날짜마다 따로 올릴 필요가 없습니다. 행마다 제 운송일자로 들어가고, 머리말이 그 파일이 덮는 날짜 범위를 보여 줍니다.",
      "MES 상행실적이 배차에 붙으려면 **운송일자·차량·상행 도착지 셋이 다 맞는 상행 배차**가 이미 있어야 합니다. 파일 한 건에 여러 날짜·여러 차량이 섞여 있으면 그만큼 배차가 필요합니다.",
      "셋이 다 같은 배차가 둘 이상이면 시스템이 고르지 않고 미매칭으로 둡니다. 아무 회차나 찍으면 틀린 배차에 붙은 채로 확정까지 갑니다 — ‘배차 고르기’ 로 직접 고르세요.",
      "배차 후보에는 **상행 실적이 아직 없는 회차만** 나옵니다. 이미 다른 행이 붙은 배차에 또 붙이면 한 배차에 상행 실적이 둘이 되어 톤수가 겹칩니다.",
      "‘배차 고르기’ 는 그 행이 **실적 대상으로 들어간 행일 때만** 나옵니다. 버튼 대신 `반영 전 · 예상` 이면 아직 반영되지 않은 것이고, `실적 대상 아님 · 반영에서 빠진 행` 이면 행 상태가 오류·중복이라 빠진 행입니다 — ‘행 상태’ 를 바꿔 확인하세요.",
      "기준정보 · 구간·장소에서 `MES 수재 대상`으로 지정한 목적지만 실적에 들어옵니다. 대상 외 상세행과 소계행은 자동으로 빠집니다.",
      "`대상 외` 와 `미해석` 은 다릅니다. 대상 외는 장소는 알지만 수재 대상이 아닌 곳이라 그냥 두면 되고, 미해석은 표준 장소·별칭 어디에도 없는 표기라 손볼 곳이 있다는 신호입니다.",
      "MES 표기가 `아세아시멘트_제천` 처럼 밑줄·괄호만 다른 경우는 자동으로 붙습니다. 별칭이 필요한 것은 `한일현대시멘트_영월`→`현대시멘트(영월)` 처럼 이름 자체가 다를 때입니다.",
      "검산은 한 줄 요약만 보여 주고, **어긋난 묶음이 있을 때만** 표가 펼쳐집니다. 전량 일치면 볼 것이 없습니다.",
      "미매칭이 남는 흔한 이유는 차량번호 표기 차이입니다. 기준정보 · 차량·기사에서 MES 차량코드를 확인하세요.",
      "품목명이 원본과 다르면 기준정보 · 품목의 품목매핑에서 이어 주세요.",
    ],
    related: [L.vehicle, L.item, L.dispatch, L.performance],
  },
  {
    pattern: "/admin/performance/closing-upload",
    title: "마감 실적 업로드",
    screenshot: "/help/performance-closing-upload.png",
    summary:
      "하행 송장 사진을 받지 않는 품목의 **월말 마감 엑셀**로 실적을 확정합니다. 올리면 그 자리에서 확정 실적이 되고 단가까지 붙어 금액이 나옵니다.",
    steps: [
      "‘파일 올리기’ 로 한중 지정 양식을 올리거나, 위 ‘업로드’ 에서 지난 파일을 고릅니다. ‘상세내역’ 시트를 읽습니다.",
      "올리는 즉시 구간을 훑습니다. **지난 파일에서 이어 둔 조합은 그대로 붙어 옵니다.**",
      "‘구간 지정’ 에 ‘미지정’ 이 남아 있으면 시스템 구간을 골라 이어 줍니다. **반영 전에 이어 주셔야 그 행까지 한 번에 들어갑니다.**",
      "행 표에서 오류가 없는지 보고 ‘실적 반영’ 을 누릅니다.",
      "결과에 업체 차량 몇 건이 운송사 실적으로 들어갔는지, 자사 차량 배차가 몇 건 새로 생겼는지 적힙니다. 잘못 올렸으면 ‘이 업로드 버리기’ 로 되돌립니다.",
    ],
    controls: [
      { name: "업로드", description: "올렸던 파일을 골라 다시 엽니다. 파일명·일시·상태로 검색되고, 고르면 새로 올린 것과 같은 화면이 나옵니다." },
      { name: "파일 올리기", description: "마감 엑셀을 고릅니다. 화면 위로 끌어다 놓아도 됩니다. 파일을 연 뒤에는 ‘새 파일 올리기’ 로 바뀝니다." },
      { name: "양식 받기", description: "빈 양식을 내려받습니다. 업체에 보낼 때 씁니다." },
      { name: "시스템 구간", description: "‘구간 지정’ 표의 이 칸에서 파일의 `상차지-하차지-품명` 표기를 시스템 구간에 잇습니다. 한 번 이어 주면 다음 달 파일은 저절로 붙습니다." },
      { name: "표준 품목", description: "그 조합의 품목입니다. 비워 두면 구간에 등록된 기본 품목을 씁니다." },
      { name: "실적 반영", description: "행을 확정 하행 실적으로 만듭니다. 이 화면의 주 동작입니다." },
      { name: "오류행 받기", description: "오류가 난 행만 사유와 함께 엑셀로 내려받습니다. 고쳐서 다시 올리세요." },
      { name: "이 업로드 버리기", description: "그 업로드가 만든 실적(자사 차량이면 배차까지)을 함께 지웁니다. 결산을 확정한 뒤에는 막힙니다." },
    ],
    notes: [
      "**한 업로드는 한 번만 반영됩니다.** 그래서 구간은 반영하기 **전에** 이어 주셔야 합니다 — 올리는 즉시 훑어 ‘구간 지정’ 표를 채우는 이유가 그것입니다. 반영 뒤에 미지정이 남은 것을 발견하셨다면 그 파일을 버리고 다시 올리세요.",
      "구간은 **운송코드 → 상차지·하차지·품명 → 담당자 지정** 순으로 찾습니다. 운송코드(`G0104` 같은)가 적혀 있으면 이름이 달라도 한 번에 붙습니다 — 단가에 등록된 코드와 대조하기 때문입니다.",
      "**운송코드만 적어 보내셔도 됩니다.** 품명·상차지·하차지가 비어 있으면 그 코드로 찾은 구간의 값을 **흐린 글씨로** 채워 보여 드립니다 — 흐린 값은 시스템이 찾은 것이고, 진한 값은 파일에 적힌 것입니다.",
      "**시트 이름은 상관없습니다.** `상세내역` 이 있으면 그 시트를, 없으면 첫 시트를 읽고 어느 시트를 읽었는지 알려 드립니다. 다만 `거래명세서` 시트가 앞에 있는 파일이면 그쪽을 먼저 읽으니, 지정 양식을 쓰실 때는 `상세내역` 이름을 그대로 두시는 편이 안전합니다.",
      "**업체 차량은 차량을 등록하지 않고 운송사 실적으로 들어갑니다.** 차가 업체를 옮겨 다니므로 매입은 차가 아니라 그 행의 `운송사CODE` 업체로 잡힙니다 — 기준정보에 없는 차량번호여도 됩니다. 코드는 기준정보 › 거래처의 `거래처코드` 에서 확인하세요. **자사 차량만** 배차 회차에 붙습니다.",
      "행 표의 **‘운송사’** 칸이 그 행이 누구 실적으로 들어갈지 보여 줍니다. 진한 글씨는 파일의 `운송사CODE` 로 찾은 업체이고, 흐린 글씨는 코드가 비어 시스템이 정한 업체입니다(위에서 고른 업체 → 파일의 다른 행 → 등록된 차량의 소속 순). 자사 차량은 ‘자사’ 입니다.",
      "표 맨 아래 **총계**는 `정상` 행만 더한 **파일 전체**의 톤수입니다. 표는 100행씩 끊어 보여도 합계는 전체를 셉니다.",
      "차량번호는 전체 번호도, 뒷 4자리도 받습니다. 업체 차량은 번호를 못 찾아도 실적이 들어가고, 같은 차가 같은 날 같은 구간을 두 번 뛰었으면 두 줄로 들어갑니다. 같은 파일을 고쳐 다시 올리면 같은 줄을 고칩니다.",
      "**자사 차량**은 그 날짜·차량·구간에 배차가 있으면 거기에 톤수를 넣고, 없으면 **배차를 새로 만들어** 붙입니다. 몇 건이 만들어졌는지는 반영 결과에 적힙니다.",
      "같은 운행이 업체 송장으로도 잡혀 있으면 그쪽 가실적을 걷어냅니다 — 톤수가 두 번 잡히지 않게 하기 위해서입니다.",
      "이미 확정된 결산에 들어간 실적은 톤수를 바꾸지 않습니다. 결산 확정을 먼저 취소하세요.",
      "파일의 안내문이 적힌 칸은 읽지 않습니다. ‘년월일’ 이 빈 줄은 데이터로 보지 않습니다.",
    ],
    related: [
      L.route,
      L.price,
      L.vehicle,
      L.performance,
      { label: "MES 상행실적 업로드", path: "/admin/performance/mes-upload", reason: "상행은 이쪽으로 올립니다" },
    ],
  },
  {
    pattern: "/admin/performance/downbound-estimate",
    title: "하행 가실적",
    screenshot: "/help/performance-downbound-estimate.png",
    summary:
      "송장 검증으로 확정되기 전까지 쓰는 하행 가실적 톤수입니다. 배차에 적은 가톤수, 없으면 구간에 등록한 가톤수를 씁니다. 평균을 내어 채우지 않습니다.",
    steps: [
      "날짜를 고르고 가실적을 확인합니다.",
      "같은 차량·구간의 과거 톤수가 궁금하면 행을 펼쳐 표본을 봅니다(참고용입니다).",
      "구간별 소계와 맨 아래 ‘구간별 총 횟수’ 로 그날 어느 구간을 몇 번 갔는지 맞춰 봅니다.",
    ],
    controls: [
      { name: "전일 / 다음일", description: "하루씩 앞뒤로 이동합니다." },
      { name: "표본 이력", description: "같은 차량·구간의 과거 실적을 펼칩니다. 참고용이고 가실적에는 쓰이지 않습니다." },
      { name: "근거", description: "가실적이 어디서 온 값인지 적습니다. ‘배차 입력’ 은 그 회차에 적은 값이고 ‘구간 가톤수’ 는 기준정보에 등록한 값입니다." },
    ],
    notes: [
      "톤수는 **담당자가 정한 값뿐입니다** — 배차에 적은 값, 없으면 구간에 등록한 가톤수(기준정보 › 단가 또는 구간·장소의 ‘가톤수’ 칸)입니다.",
      "근거가 ‘기본 29톤’ 이면 배차에 적은 값도, 그 구간에 등록된 가톤수도 없다는 뜻입니다. 다른 값을 쓰시려면 단가 화면에서 그 구간의 가톤수를 채워 주세요.",
      "여기 값은 어디까지나 추정입니다. 결산은 송장으로 확정된 실제 톤수로 냅니다.",
      "‘최근 평균’ 과 ‘표본 이력’ 은 참고용입니다. 표본이 3건 이상일 때 중앙값 대비 편차가 30%를 넘으면 이상치로 표시합니다.",
      "품목이 비어 있으면 그 구간에 등록된 기본 품목을 보여 줍니다.",
      "맨 아래 ‘구간별 총 횟수’ 표가 같은 구간의 자차와 업체를 한 줄에 놓고 합계를 냅니다. 여기 **자차는 운행 횟수**라 같은 차가 하루 두 번 뛰면 2로 셉니다 — 위 회차 표의 ‘대수’ 와 숫자가 다를 수 있습니다.",
      "‘대수’ 는 같은 차를 한 번만 셉니다 — 한 차가 하루 두 번 뛰면 2행 1대입니다. 업체는 어느 차가 뛸지 모르므로 업체에 넘긴 지시 대수로 셉니다.",
    ],
    related: [L.invoice, L.route, L.dispatch],
  },
];

// ---------------------------------------------------------------------------
// 송장내역확인
// ---------------------------------------------------------------------------

const INVOICE: HelpTopic[] = [
  {
    pattern: "/admin/invoice",
    title: "송장 제출현황",
    screenshot: "/help/invoice.png",
    summary:
      "하루치 송장 제출 상태를 보는 화면입니다. 하행 회차 1건에는 상차 송장 1장과 하차 송장 1장이 필요합니다.",
    steps: [
      "날짜 이동으로 볼 날짜를 고릅니다.",
      "미제출·검증대기 줄을 확인합니다.",
      "썸네일을 누르면 사진을 크게 봅니다. 거기서 확대·회전할 수 있고 ‘상세보기’ 로 검수까지 갑니다.",
      "줄 오른쪽 ‘상세보기’ 를 누르면 검수 화면으로 넘어갑니다.",
      "기사나 업체가 못 올린 송장은 ‘송장 등록’ 탭에서 담당자가 대신 올립니다.",
      "제출이 늦으면 아래 ‘미제출 회차’ 의 ‘기사에게 알림 보내기’ 로 기사 앱에 알림을 띄웁니다.",
    ],
    controls: [
      { name: "송장 목록 / 송장 등록", description: "머리말 아래 탭입니다. ‘송장 목록’ 은 올라온 송장을 보고 검수로 넘어가는 곳이고, ‘송장 등록’ 은 담당자가 대신 올리는 곳입니다. 두 탭이 같은 날짜를 봅니다." },
      { name: "기사 N장 / 업체 N장 / 기사 N줄 / 업체 N줄", description: "두 탭 모두 날짜 오른쪽에 있습니다. 개인 차주(기사)와 업체 송장을 한 표에 섞지 않고 갈라 봅니다. 고르지 않은 쪽에도 장수(등록 탭은 줄 수)가 적혀 있어 다른 쪽에 몇 장이 와 있는지 바로 보입니다. 고른 값은 두 탭이 함께 씁니다. ‘필요·제출’ 도 고른 쪽 몫만 셉니다. 아래 ‘미제출 회차’ 는 기사 회차를 세는 것이라 ‘기사’ 쪽에서만 보입니다. **상차·하차 둘 다 안 온 회차만** 나옵니다 — 실적은 구간의 기준 송장 한 장으로 확정되므로 한 장이라도 왔으면 빠집니다." },
      { name: "배차 없이 올리기", description: "**배차가 아직 없어도 올립니다** — 주말처럼 배차를 미리 못 넣은 날이나, 어느 배차인지 모르는 사진을 받았을 때 쓰세요(기사·업체 포털은 처음부터 그렇게 올립니다). `미분류` 로 들어가고 **붙을 자리는 시스템이 찾습니다** — 차량·일자로 회차를 찾으면 그 회차, 차량 소속이 업체면 업체귀속, 아무것도 못 찾으면 ‘미매칭·중복·반려’ 화면에 모입니다. 배차를 등록한 뒤 거기서 ‘재매칭’ 을 누르면 붙습니다. **이 표에는 배차 줄로만 묶여 보이므로 안 붙은 장은 여기 안 보입니다.**" },
      { name: "사진 고르기", description: "‘송장 등록’ 탭의 배차 줄마다 있습니다. 여러 장을 한꺼번에 고르면 버튼을 더 누르지 않아도 바로 올라갑니다 — 골라 놓고 안 눌러 안 올라간 줄 모르는 일을 막습니다." },
      { name: "상차 / 하차", description: "올린 장을 분류합니다. 아무것도 미리 골라 두지 않습니다 — 사진을 보지 않고 정하면 상차 사진이 하차로 확정돼 톤수가 조용히 잘못 잡힙니다. 분류해야 검증할 수 있습니다." },
      { name: "삭제", description: "잘못 올린 장을 그 자리에서 치웁니다 — **사진 파일까지 지워지고 되돌릴 수 없어** 확인 창이 한 번 묻습니다. **세 자리에 있습니다** — 이 화면(배차 줄을 펼친 칸), ‘송장 목록’ 탭의 행, 송장 상세. 배차가 안 붙은 장은 이 화면에 안 보이니 목록이나 상세에서 치우세요. 반려와 다릅니다: 반려는 다시 올리라는 신호로 장은 남습니다. 검증이 끝난 장은 실적의 근거라 삭제되지 않습니다(확정을 먼저 취소하세요)." },
      { name: "상세보기", description: "그 송장의 검수 화면으로 이동합니다. ‘송장 목록’ 표의 오른쪽 끝과, 사진을 크게 본 창 위쪽에 있습니다. 돌아오면 보던 날짜가 그대로 유지됩니다." },
      { name: "상세", description: "‘송장 등록’ 탭에서 펼친 줄의 송장마다 있습니다. 검수 화면으로 바로 갑니다 — 여기서 할 수 있는 것은 구분과 차량뿐이고 톤수 확정·매칭 수정은 검수 화면에 있습니다." },
      { name: "기사에게 알림 보내기", description: "‘기사’ 쪽 ‘미제출 회차’ 제목 옆에 있습니다. 그 회차의 기사에게 *몇 회차 어느 송장을 올려 달라* 는 알림을 보냅니다 — 기사 앱 하단 ‘알림’ 탭에 숫자 뱃지가 붙고, 거기서 ‘송장 올리기’ 를 누르면 그 회차의 제출 화면이 열립니다. **푸시·문자는 가지 않아** 기사가 앱을 열어야 보입니다. 여러 번 눌러도 기사 화면에 같은 줄이 쌓이지 않고, 기사가 한 장이라도 올리면 알림은 저절로 내려갑니다. 기사가 안 정해진 배차는 보낼 곳이 없어 건너뛰고 그 수를 알려 줍니다." },
      { name: "엑셀 다운로드", description: "지금 조회된 목록을 CSV 로 내려받습니다." },
      { name: "인쇄", description: "브라우저 인쇄 창을 엽니다." },
    ],
    notes: [
      "상행에는 송장이 없습니다. MES 업로드로 확정됩니다.",
      "‘송장 등록’ 탭은 기사 배차와 업체 배차를 ‘기사 / 업체’ 로 갈라 봅니다. 기사가 앱을 못 쓰거나(폰 고장·계정 잠김) 업체가 포털을 쓰지 않고 카톡·메일로 보내온 경우 담당자가 대신 올립니다. 기사 줄은 회차가 차량을 정하고 있어 차량을 고를 칸이 없습니다.",
      "업체 송장은 **업체 귀속이나 업체 배차 지시가 붙은 장**입니다. 누가 올렸는지가 아니라 어느 쪽 매입으로 가는지로 가릅니다 — 담당자가 업체 대신 올린 장도 업체 쪽에 보입니다.",
      "두 탭 모두 썸네일을 누르면 원본을 크게 봅니다. 40px 썸네일로는 계량전표의 숫자를 읽을 수 없어, 확인하려고 검수 화면까지 들어갔다 나오지 않아도 됩니다. 바깥을 누르거나 Esc 로 닫습니다.",
      "검수 화면으로 갈 때 조회 일자가 주소에 남습니다 — 돌아오면 보던 날짜와 보던 탭이 그대로입니다.",
      "반려 뒤 기사가 다시 올려도 줄이 늘지 않습니다. ‘제출’ 칸에 ‘새 제출본’ 이 붙으니 상세에서 사진을 골라 검수 대상으로 지정하세요.",
    ],
    related: [
      { label: "미매칭·중복·반려", path: "/admin/invoice/unmatched", reason: "배차에 붙지 못한 송장을 모아 둡니다" },
      L.dispatch,
      L.performance,
    ],
  },
  {
    pattern: "/admin/invoice/unmatched",
    title: "미매칭·중복·반려",
    screenshot: "/help/invoice-unmatched.png",
    summary:
      "배차에 붙지 못했거나 중복으로 잡힌 송장, 반려되어 재제출을 기다리는 송장을 모아 둡니다.",
    steps: [
      "‘미매칭’ / ‘중복’ / ‘반려’ 탭을 고릅니다.",
      "미매칭이 많으면 ‘일괄 재매칭’ 을 한 번 돌립니다.",
      "그래도 남는 건은 줄을 눌러 검수 화면에서 직접 배차에 잇습니다.",
    ],
    controls: [
      { name: "미매칭 / 중복 / 반려", description: "처리해야 할 종류를 나눠 보는 탭입니다." },
      { name: "일괄 재매칭", description: "미매칭 송장을 다시 배차에 맞춰 봅니다." },
    ],
    notes: [
      "미매칭이 남는 흔한 이유는 차량번호·장소명 표기 차이입니다.",
      "장소명이 흔들리면 기준정보 · 장소의 별칭에 등록해 두면 다음부터 자동으로 붙습니다.",
    ],
    related: [L.location, L.vehicle, L.invoice],
  },
  {
    pattern: "/admin/invoice/:id",
    title: "송장 검수",
    screenshot: "/help/invoice-detail.png",
    summary:
      "송장 이미지를 보며 OCR 이 읽은 값을 확인하고 확정하는 화면입니다. 하행 실적은 여기 검증으로 확정됩니다.",
    steps: [
      "왼쪽 이미지와 오른쪽 ‘값 대조’ 표를 견줍니다.",
      "확정값은 오른쪽 끝 칸에 직접 넣거나, 송장·배차 칸의 **밑줄 그어진 값을 눌러** 넣습니다.",
      "다 채웠으면 ‘저장’ 합니다.",
      "OCR 이 잘못 읽었으면 ‘다시 읽기’ 를 누릅니다.",
      "배차에 안 붙어 있으면 배차를 찾아 ‘연결’ 합니다.",
      "맞으면 ‘검증 완료’, 못 쓸 송장이면 ‘반려’ 를 누릅니다.",
    ],
    controls: [
      { name: "검증 완료", description: "송장을 검증 완료로 확정합니다. 이 화면의 주 동작입니다." },
      { name: "반려", description: "사유를 적어 기사에게 재제출을 요청합니다. **장은 남습니다** — 사진까지 지우려면 ‘삭제’ 입니다." },
      { name: "삭제", description: "잘못 올린 이 장을 지웁니다 — **사진 파일까지 지워지고 되돌릴 수 없어** 확인 창이 한 번 묻습니다. 지우면 목록으로 돌아갑니다. 반려와 가려 쓰세요: 반려는 다시 올리라는 신호로 장을 남깁니다. 검증이 끝난 장은 실적의 근거라 버튼이 보이지 않습니다(확정을 먼저 취소하세요)." },
      { name: "보류", description: "판단을 미루고 남겨 둡니다." },
      { name: "다시 읽기", description: "이미지를 OCR 로 다시 읽습니다." },
      { name: "저장", description: "확정값과 고친 값을 저장합니다. 누르기 전에는 반영되지 않습니다." },
      {
        name: "확정값으로",
        description: "차량번호 칸에 적은 글자를 기준정보 차량으로 찾아 확정값에 넣습니다.",
      },
      {
        name: "청구처",
        description:
          "이 운송을 **어느 거래처 앞으로 청구할지**입니다. 그 구간·품목에 매출단가가 한 거래처에만 걸려 있으면 저절로 채워지므로 보통 손댈 일이 없습니다. **단가가 둘 이상인 구간에서는 비어 있고, 그때 직접 골라야 금액이 붙습니다** — 비운 채 검증하면 검증은 되지만 단가를 고를 수 없어 금액이 빕니다. 고른 값은 배차 회차에도 남아 배차 화면에서 그대로 보입니다 — `저장` 과 `검증 완료` 둘 다 반영됩니다(회차가 없는 업체 송장만 `검증 완료` 때 반영됩니다).",
      },
      { name: "연결", description: "이 송장을 고른 배차 회차에 잇습니다." },
      { name: "중복 아님 / 중복 제외", description: "중복으로 잡힌 건을 정리합니다." },
      { name: "배차 상세", description: "이어진 배차의 운행 회차 관리로 이동합니다." },
      { name: "제출현황으로 / 목록", description: "송장 목록으로 돌아갑니다." },
      {
        name: "보기",
        description:
          "제출 이력 표에 있습니다. 그때 올라온 사진을 위 뷰어에 띄웁니다. 검수 대상은 바뀌지 않습니다.",
      },
      {
        name: "검수 대상으로",
        description:
          "그 사진을 검증의 근거로 삼습니다. 지정하면 그 사진으로 인식을 다시 돌립니다.",
      },
    ],
    notes: [
      "반려하면 기사 화면에 사유가 그대로 보입니다. 무엇을 다시 찍어야 하는지 적어 주세요.",
      "재제출된 송장은 줄이 새로 생기지 않고 사진이 이력에 쌓입니다. 이미지 아래 ‘제출 이력’ 표에서 제출 시각으로 골라 보세요.",
      "새 사진이 올라와도 검수 대상은 저절로 바뀌지 않습니다. 사진을 확인한 뒤 ‘검수 대상으로’ 를 눌러 지정하고 검증하세요.",
      "반려한 사진이 검수 대상인 채로는 검증할 수 없습니다. 재제출본을 검수 대상으로 지정한 뒤 검증합니다.",
      "검증 완료된 톤수가 하행 실적의 확정값이 됩니다.",
      "검증이 곧 확정이라 **금액도 여기서 결정됩니다.** `청구처` 가 비어 있으면 매출단가를 고를 수 없어 금액이 빈 채로 확정됩니다 — 검증 뒤 ‘청구처가 정해지지 않아…’ 안내가 뜨면 단가를 또 등록하지 마시고 청구처를 지정하세요. 이미 확정된 건은 `결산 검증 › 대상미정` 에서 청구처를 고르고 ‘금액 재계산’ 을 누릅니다.",
      "밑줄이 없는 값은 확정값에 넣을 수 없습니다 — 배차에 없거나 기준정보에서 못 찾은 이름입니다.",
    ],
    related: [L.dispatch, L.performance, L.location],
  },
];

// ---------------------------------------------------------------------------
// 결산업무
// ---------------------------------------------------------------------------

const SETTLEMENT: HelpTopic[] = [
  {
    pattern: "/admin/settlement/summary",
    title: "매출·매입 집계표",
    screenshot: "/help/settlement-summary.png",
    summary:
      "확정된 실적을 업체 · 품목 · 구간으로 묶어 보는 집계표입니다. 월 · 분기 · 반기 · 연 단위로도, 시작일~종료일을 직접 정해서도 봅니다.",
    steps: [
      "기간을 고릅니다. ‘월 · 분기 · 반기 · 년’ 버튼을 누르면 시작일과 종료일이 그 단위로 채워집니다.",
      "‘매출’ / ‘매입’ 탭을 고릅니다.",
      "표 위의 결산 검증 경고를 먼저 확인합니다. 경고 줄의 ‘자세히’ 를 누르면 그 경고에 걸린 실적이 집계표 자리에 펼쳐지고, 단가 등록·확정까지 그 자리에서 합니다. ‘미확정실적’ 은 체크한 줄을 한 번에 확정할 수 있고, ‘비고’ 에 아직 확정되지 않은 사유(송장 미제출·미검증·MES 미반영·청구처 없음 등)가 적힙니다. 청구처 목록은 그 구간에 단가가 있는 곳이 맨 앞이고 단가가 곁에 적히며, 고르면 단가가 바로 붙고 배차 회차(업체 지시 가실적이면 그 지시)에도 저장됩니다. 단가는 매출·매입(기사)·매입(업체) 셋이 다 보이고, 이 실적의 매입 대상이 아닌 칸은 같은 구간의 지금 단가를 흐리게 보입니다. ‘집계표 보기’ 로 돌아옵니다.",
      "금액이 맞으면 거래명세서·세금계산서 화면에서 결산을 확정합니다.",
    ],
    controls: [
      { name: "엑셀 다운로드", description: "레거시 집계표 서식으로 내려받습니다. 업체·품목은 세로로 병합됩니다. **지금 고른 보기 그대로** 받습니다 — 분류별이면 분류·업체가 병합됩니다. ‘운송사’ 를 골랐으면 화면처럼 **구간마다 일자·차량별 줄을 펼친 꼴**(레거시 운송사별 집계표)로 받습니다. 파일 이름에 걸린 조건(운송사·품목·방향·가실적 포함)이 붙습니다." },
      { name: "운송사", description: "‘매입’ 탭에만 있습니다. 그 기간에 실적이 있는 매입처(업체·개인차주)만 목록에 나옵니다. 고르면 그 운송사의 **모든 품목·구간이 오른쪽 상세 창에 열려** 일자·차량별 운송량·단가·금액이 바로 보입니다. **운송사를 고르면 ‘가실적 포함’ 이 저절로 켜집니다** — 송장만 검증했거나 마감 업로드만 한 실적도 적힌 그대로 보입니다. 확정분만 보려면 체크를 끕니다. 목록에도 확정 전 실적만 있는 업체까지 나옵니다. ‘분류별’ 보기에서는 업체 칸을 눌러 엽니다." },
      { name: "업체별 / 분류별", description: "표를 묶는 기준입니다(고객 요청 2026-10-19). ‘업체별’ 은 업체 > 품목 > 구간, ‘분류별’ 은 분류 > 업체 > 구간이고 레거시 영업부 매출·매입집계표가 이 꼴입니다. 같은 숫자를 다르게 묶을 뿐이라 총계는 같습니다. 고른 보기는 다음에 열어도 그대로입니다." },
      { name: "나눠 받기", description: "지금 조건 그대로 **업체(운송사·청구처)마다 · 품목마다 · 달마다** 파일을 하나씩 나눠 ZIP 으로 받습니다 — 업체마다 메일로 따로 보낼 때 씁니다. 연도로 조회하고 ‘달마다’ 를 켜면 1월~12월이 한 번에 나옵니다. 파일 하나는 그 업체·품목을 골라 ‘엑셀 다운로드’ 한 것과 같습니다. 실적이 없는 조합은 파일을 만들지 않습니다. 창에서 **받을 거래처만 체크**할 수 있습니다." },
      { name: "인쇄", description: "표 영역만 A4 가로로 인쇄합니다. 상세 창이 열려 있으면 닫고 전체 칸으로 인쇄합니다." },
      { name: "닫기", description: "오른쪽 상세 창을 닫습니다. Esc 로도 닫힙니다. 같은 칸을 한 번 더 눌러도 닫힙니다." },
      { name: "포함", description: "가실적까지 포함해 미리 봅니다. 기본은 확정 실적만 집계합니다." },
      { name: "월 · 분기 · 반기 · 년", description: "시작일이 속한 달·분기·반기·해로 기간을 한 번에 채웁니다. 기본은 지난달입니다." },
      { name: "이전 · 다음", description: "고른 단위만큼 앞뒤로 옮깁니다. 날짜를 직접 정했을 때는 같은 일수만큼 옮깁니다." },
      { name: "시작일 · 종료일", description: "직접 고치면 그 구간으로 조회합니다. 이때 표와 명세서의 기간 표기는 ‘2026-08-05 ~ 2026-08-20’ 처럼 날짜로 찍힙니다." },
      { name: "집계표 보기", description: "결산 검증 상세를 닫고 집계표로 돌아갑니다. 상세는 집계표와 **같은 자리**를 번갈아 씁니다 — 겹쳐 띄우면 뒤의 표가 가려져 대조가 안 되기 때문입니다. 상세를 보는 동안에도 ‘인쇄’ 와 ‘엑셀 다운로드’ 는 집계표를 냅니다." },
      { name: "톤수 조정", description: "월별 조회일 때만 나옵니다. 수분 감량처럼 마감할 때 톤수를 한꺼번에 빼거나 더합니다 — 음수가 감량이고 대당(회당) 단위입니다. 그 달 전체에 쓸 값을 한 줄 넣고, 다른 구간만 줄을 더해 덮습니다." },
      { name: "적용범위", description: "조정치를 걸 구간입니다. 비우면 **그 달 전체**에 적용되고, 구간을 고르면 그 구간만 예외로 덮습니다 — 매출단가의 거래처별 예외와 같은 방식입니다. 지금 집계표에 떠 있는 구간이 **맨 위에 `이 기간 실적` 꼬리표와 함께** 옵니다. 목록에서 빠지지는 않으니 그 달에 안 뛴 구간에도 미리 걸어 둘 수 있습니다. **미사용으로 돌린 구간도 그 기간 실적이 있으면 `미사용` 꼬리표를 달고 나옵니다** — 지난달 실적에 조정치를 걸 수 있어야 하기 때문입니다." },
      { name: "품목", description: "구간을 고른 뒤 품목까지 고르면 **그 구간 그 품목만**의 조정치입니다 — 같은 구간도 품목마다 감량이 다를 때 씁니다(예: 석회석소괴 -0.4 · 석회석분광 -0.7). 비우면 그 구간 전 품목이고, 품목 줄이 구간 줄을 덮습니다. 그 구간에 단가가 걸린 품목이 `이 구간 단가` 로 위에 섭니다." },
      { name: "조정치 저장", description: "그 달 조정치를 넣거나 덮어씁니다. 집계표 톤수·금액에 바로 반영되고, 이미 확정한 정산은 다시 확정해야 명세서에 반영됩니다." },
    ],
    notes: [
      "공급가액 = 내림(톤수 × 단가), 세액 = 내림(공급가액 ÷ 10) 입니다. 회당 요율이면 횟수 × 단가입니다.",
      "**포스코 수재(상행) 매입은 한 달 구간 톤수의 소수점을 버려 정산합니다**(업체·개인차주 같음, 매출은 그대로). 그런 구간·소계·총계는 **두 줄**로 섭니다 — 윗줄이 소수점을 버린 정산 톤수, 아랫줄(`소수점 포함`)이 소수점까지 나온 톤수이고 금액은 두 줄이 같습니다(정산 톤수 × 단가). 엑셀도 같은 꼴입니다. 매입 거래명세서도 같은 값입니다(명세에는 `소수점 정산` 차이 줄이 붙습니다). 오른쪽 상세 창의 일별 내역은 실제 톤수라 합이 정산 값과 조금 다를 수 있습니다.",
      "‘조정’ 열은 그 달 톤수 조정분입니다. ‘톤수’ 는 조정을 반영한 확정(마감) 톤수이고, 조정 전 톤수는 둘을 빼면 나옵니다. 조정은 이 표와 거래명세서에만 반영되고 일일실적은 그대로입니다.",
      "매출은 도착지(청구처) 기준, 매입은 운송회사·차주 기준입니다. 톤수는 같고 단가만 다릅니다.",
      "한 칸이 여러 실적의 합이라 여기서는 값을 고칠 수 없습니다. 값을 고치려면 행 오른쪽 ‘일일실적’ 으로 가세요.",
      "**매입은 업체와 자차(개인차주)가 갈려 있습니다** — 업체가 먼저 오고 그 아래 선 하나를 두고 자차가 모입니다. 둘 다 있으면 ‘업체 소계’·‘개인차주 소계’ 가 총계 위에 붙습니다. 업체마다의 소계 줄은 회색 바탕으로 칠해 둡니다. 가르는 기준은 차량이 아니라 **매입을 어디로 치르느냐**입니다 — 운송회사 앞으로 나가면 업체, 개인차주 앞으로 나가면 자차입니다. 거래명세서가 나가는 축과 같습니다.",
      "**누르면 오른쪽 상세 창에 일별 내역이 나옵니다** — 누른 칸이 범위를 정합니다. `업체명` 칸은 그 업체의 모든 품목, `품목` 칸은 그 업체의 그 품목, 구간 줄은 그 구간 하나입니다. 창에는 구간마다 일자·차량번호·횟수·톤수·단가·금액과 소계가 나오고, 둘 이상이면 맨 아래 합계가 붙습니다. 왼쪽 목록은 그대로 남아 있어 다른 업체·품목을 누르면 창 내용만 바뀝니다. 창이 열려 있는 동안 왼쪽 표는 숫자 칸을 ‘합계’ 만 남깁니다. 월말 톤수 조정까지 같은 식으로 계산하므로 창의 소계는 그 구간 행과 맞습니다. 차량번호가 ‘—’ 인 줄은 업체가 어느 차를 넣었는지 적지 않은 운행입니다.",
      "**분류별 보기의 분류는 월말 실적의 하행 소계 분류와 같습니다** — 상행(수재)이 먼저 오고, 하행은 소계 분류의 순서(클링커 · 석회석 · 기타 …)입니다. 분류 안에 품목이 여럿이라 품목 칸이 하나 더 있습니다. 분기·반기로 보면 한 구간이 달마다 다른 분류에 설 수 있고(‘이번 달만’ 지정), 상세 창에는 그 분류였던 달만 나옵니다. 업체 소계는 그 분류 안에서 줄이 둘 이상일 때만 붙습니다.",
      "경고 상세는 최대 200건까지 한 번에 보여 줍니다. 그보다 많으면 ‘먼저 나온 N건만 보입니다’ 라고 적히고, 처리하면 다음 건이 이어서 나옵니다.",
      "‘기간중복’ 만 펼칠 수 없습니다. 실적이 아니라 이미 확정된 정산과 기간이 겹치는 것이라 모아 보여 줄 실적이 없습니다 — 거래명세서 화면에서 확정 범위를 조정합니다.",
    ],
    related: [L.price, L.performance, L.company],
  },
  {
    pattern: "/admin/settlement",
    title: "거래명세서·세금계산서",
    screenshot: "/help/settlement.png",
    summary:
      "결산을 확정하고, 확정된 정산에서 업체별 거래명세서와 세금계산서 양식을 발급하는 화면입니다.",
    steps: [
      "기간과 ‘매출’ / ‘매입’ 을 고릅니다. 기간은 집계표와 같은 방식입니다 — 버튼으로 월·분기·반기·년을 채우거나 시작일~종료일을 직접 정합니다.",
      "‘결산 확정’ 을 누릅니다. 정산을 굳히는 동시에 거래명세서와 세금계산서를 업체별로 한꺼번에 만듭니다.",
      "업체 줄을 누르면 그 업체의 명세가 **오른쪽 상세 창**에 나옵니다. **결산 확정 전에도 볼 수 있습니다** — 아직 결산하지 않은 업체는 `확정 전` 줄로 서고, 그때까지 확정된 실적으로 묶은 명세가 나옵니다. 목록은 왼쪽에 남아 있어 다른 업체 줄을 누르면 바로 바뀝니다. 업체 줄의 ‘다운로드’ 로 파일을 받아 업체에 전달합니다.",
      "**매출 거래명세서의 상세 창은 받은 파일과 같은 꼴입니다** — 품목·구간·단가 묶음마다 날짜순 줄, 그 아래 `소계`, 맨 끝 `합계` 이고 **금액이 큰 묶음부터** 섭니다. 매입 거래명세서는 일일실적과 같은 순서(수재 → 클링커 → 석회석 → 기타)이고, **상세 창도 매출과 같은 꼴**(묶음마다 날짜순 줄 → 소계 → 합계)입니다. 받는 매입 파일은 예전 양식대로 날짜 줄 없이 품목·구간 소계로 나갑니다. 단가·금액·부가세는 소계·합계에만 적힙니다. 묶음이 하나면 소계 없이 합계에 단가가 적힙니다. **상세 창은 읽기 전용입니다** — 결산을 확정한 뒤에도 여기서 고치지 않습니다. 값이 틀렸으면 ‘결산 확정 취소’ 뒤 일일실적·단가에서 고치고 다시 확정합니다. 명세서 양식(차량번호·대수·비고) 선택은 파일에만 걸리고 상세 창은 늘 실적 한 건이 한 줄입니다.",
      "눈으로 훑기만 하려면 ‘통합 파일 받기’ 나 ‘거래처별 파일 받기’ 로 바로 내려받습니다. 이쪽은 발급 이력을 남기지 않습니다.",
      "세금계산서 탭에서는 홈택스로 발행한 뒤 그 업체 줄에 발행 상태·발행일자·계산서 번호를 적어 둡니다.",
    ],
    controls: [
      { name: "실적 한 건씩 보기 / 업체별 보기", description: "표 오른쪽 위에서 **보는 단위**를 바꿉니다. `업체별` 은 업체마다 한 줄, `실적 한 건씩` 은 기간 전체 업체의 실적을 한 건씩 펼친 것입니다. **확정 전에도 볼 수 있고 아무것도 저장하지 않습니다.** 아직 확정하지 않은 대상은 `확정하면 이 값` 으로 지금 실적에서 계산해 보여 주고, **이미 확정한 대상은 `확정된 명세` 로 나간 문서 그대로** 보여 줍니다 — 다시 계산하지 않습니다. 조정치가 걸린 달이면 계산 쪽에 **조정 전 · 조정 · 조정 후 톤수**가 함께 나오니, 확정하기 전에 제대로 얹혔는지 여기서 봅니다." },
      { name: "톤수 조정 적용", description: "명세 목록 표 위에 `톤수 조정 적용 — 기본 -0.5톤/회` 처럼 적힙니다. 그 달에 걸린 조정치가 명세 톤수에 이미 얹혀 있다는 뜻입니다 — 구간별 예외가 있으면 이어 붙습니다. 월별 조회일 때만 나옵니다." },
      { name: "전체 확정 취소", description: "그 기간·구분에서 **확정된 정산을 모두** 가집계로 되돌립니다. 사유가 필요하고, 정산마다 이력이 남습니다. 하나라도 실패하면 전부 되돌아갑니다. 명세 행은 지우지 않습니다 — 이미 발급한 문서의 근거입니다." },
      { name: "결산 확정", description: "기간의 확정 실적을 정산 스냅샷으로 굳히고, 거래명세서와 세금계산서를 업체별로 함께 만듭니다. 이후 단가가 바뀌어도 이 금액은 변하지 않습니다. 같은 기간을 다시 확정하면 덮어쓰고, 이미 확정된 정산과 기간이 겹치면 막습니다." },
      { name: "거래명세서 / 세금계산서 만들기", description: "확정 정산 전부를 문서로 만듭니다. 이미 있으면 문서번호는 그대로 두고 버전만 올립니다. 명세를 고친 뒤나, 다른 탭에서 확정해 이 탭 문서가 아직 없을 때 씁니다." },
      { name: "닫기", description: "업체 줄을 누르면 열리는 **오른쪽 상세 창**을 닫습니다. 창에는 그 업체 문서에 담기는 내용(기간·문서번호·명세 행·합계)이 나옵니다. **여기서는 값을 고치지 않습니다**(확정 취소 → 실적·단가 → 다시 확정). 창 위의 ‘다운로드’ 로 바로 받고 ‘문서 삭제’ 로 잘못 만든 문서를 지웁니다. 창이 열려 있는 동안 왼쪽 목록은 업체·상태·합계만 보입니다. 같은 줄을 다시 누르거나 Esc 로도 닫힙니다." },
      { name: "발행 상태 · 발행일자 · 계산서 번호", description: "세금계산서 탭에만 나오는 칸입니다. 홈택스 화면을 보며 그 업체 줄에 그대로 적습니다. 칸을 누르면 그 자리에서 고쳐집니다." },
      { name: "통합 파일 받기", description: "업체마다 시트 한 장씩 담은 파일 하나를 받습니다. 레거시 명세서 원본과 같은 모양이며 발급 이력은 남지 않습니다. 결산 전 업체도 `(확정 전)` 을 붙여 함께 들어갑니다." },
      { name: "차량번호 / 대수 / 비고", description: "‘매출’ 거래명세서 양식입니다(매출처마다 원하는 꼴이 달라서요). **차량번호를 끄면** 그 칸에 품목이 서고 같은 날 같은 구간은 한 줄로 묶입니다. **대수를 켜면** 차량번호 옆에 대수 칸이 생기고 합계에 대수 합이 나옵니다. **비고를 켜면** 맨 끝에 빈 칸이 생깁니다 — 내용은 받은 엑셀에 적으시면 됩니다. 받는 모든 파일(업체 줄 다운로드 · 통합 · 거래처별 · 확정 전)에 걸리고, 고른 값은 이번 접속 동안 기억합니다. 매입 명세서는 품목·구간 소계라 해당하지 않습니다." },
      { name: "거래처별 파일 받기", description: "업체마다 파일 하나씩 만들어 압축 파일로 받습니다. 그대로 업체에 보낼 수 있고 발급 이력은 남지 않습니다. 결산 전 업체도 `(확정 전)` 을 붙여 함께 들어갑니다." },
      { name: "확정 취소", description: "정산을 가집계로 되돌립니다. 사유가 필요하며 명세 행과 이미 발급한 문서는 지우지 않습니다." },
      { name: "다운로드", description: "그 업체 문서를 내려받습니다. 아직 만들어지지 않았으면 그 자리에서 만들어 받습니다 — ‘생성’ 을 따로 누를 필요가 없습니다. `확정 전` 줄은 그때까지 확정된 실적으로 만든 명세를 받고, 발급 이력은 남지 않습니다." },
      { name: "금액 재계산", description: "검증 경고 `대상미정` 을 펴면 행마다 있습니다. 그 줄의 청구처(매입은 운송회사)를 먼저 고른 뒤 누르면 **그 일자의 금액을 최신 단가로 다시 계산합니다.** 확정 실적의 금액은 확정 시점 스냅샷이라, 청구처만 고치면 0 원으로 남기 때문입니다. 청구처를 고르기 전에는 눌리지 않습니다." },
    ],
    notes: [
      "확정하지 않으면 문서를 만들 수 없습니다. 확정 시점 금액을 굳혀야 이미 나간 명세서와 화면 금액이 어긋나지 않습니다.",
      "가집계 줄에서는 ‘다운로드’ 와 ‘확정 취소’ 가 눌리지 않습니다. 버튼에 마우스를 올리면 왜 그런지 알려 줍니다.",
      "세금계산서 탭에는 횟수·톤수를 넣지 않았습니다. 계산서에 찍히지 않는 값이고, 물량은 업체 줄을 눌러 상세 창에서 명세 행과 합계로 봅니다.",
      "단가가 비었거나 아직 확정되지 않은 실적이 남아 있으면 확정이 막힙니다. 금액이 틀린 문서가 업체로 나가면 안 되기 때문입니다.",
      "`대상미정` 은 청구처가 정해지지 않은 확정 실적입니다. 그 구간·품목에 매출단가가 둘 이상이면 시스템이 고르지 않으므로 여기서 고르고 ‘금액 재계산’ 을 누르세요. **단가를 더 등록할 일이 아닙니다** — 다음부터 저절로 정해지게 하려면 기준정보 › 단가에서 쓰지 않는 쪽을 ‘미사용’ 으로 돌리거나, 배차 등록·송장 검수의 `청구처` 칸에서 미리 골라 둡니다.",
      "매출은 도착지(청구처) 기준, 매입은 운송회사 기준으로 묶입니다. 개인차주 정산은 거래처가 없어 문서를 발급할 수 없습니다.",
      "세금계산서는 엑셀 양식 생성과 발행 상태 관리까지입니다. 국세청 전자발행 연동은 범위 밖입니다.",
      "세금계산서 양식 원본을 아직 받지 못해 국세청 표준 서식으로 근사해 두었습니다.",
      "명세서 서식은 클라이언트 원본을 그대로 재현했습니다. 매출은 건별 상세(날짜·운송구간·차량번호·중량), 매입은 품목별 소계입니다.",
      "명세서에 찍히는 공급자 정보와 회사 직인은 기준정보 › 자사정보에서 관리합니다. 직인은 그 화면의 ‘이미지 올리기’ 로 등록하며 PNG·JPG 2MB 이하, 배경이 투명한 PNG 를 권장합니다. 등록해 두면 모든 거래명세서에 찍힙니다 — 매출은 공급받는자 칸에 겹쳐, 매입(업체·개인 차주)은 ‘귀하’ 옆에 찍히고, 없으면 도장 없이 나갑니다.",
      "발급과 받아보기는 다릅니다. 발급은 문서번호를 매기고 이력에 남지만, 통합·거래처별 받기는 파일만 내려줍니다.",
    ],
    related: [
      { label: "매출·매입 집계표", path: "/admin/settlement/summary", reason: "확정 전에 금액을 먼저 대조합니다" },
      L.profile,
      L.company,
      L.price,
      L.performance,
    ],
  },
  {
    pattern: "/admin/statistics",
    title: "통계",
    screenshot: "/help/statistics.png",
    summary:
      "월·분기·연 단위 추이와 품목·업체·차량·기사별 비중을 보는 화면입니다. 집계표와 같은 실적에서 계산합니다.",
    steps: [
      "‘기간별 추이’ 또는 ‘품목·업체별 비중’ 을 고릅니다.",
      "시작일·종료일을 정하고, 추이는 기간 단위를, 비중은 집계 기준을 고릅니다.",
      "필요하면 엑셀로 내려받습니다.",
    ],
    controls: [
      { name: "기간별 추이", description: "구간마다 톤수·운행 횟수·매출·매입·이익과 전기 대비 증감을 봅니다." },
      { name: "품목·업체별 비중", description: "품목·업체·차량·기사 중 하나를 골라 매출 기준 비중을 봅니다." },
      { name: "엑셀 다운로드", description: "지금 보고 있는 표를 그대로 내려받습니다." },
      { name: "인쇄", description: "표 영역만 A4 가로로 인쇄합니다." },
    ],
    notes: [
      "이익 = 매출 − 매입, 이익률 = 이익 ÷ 매출입니다. 비중은 매출 기준입니다.",
      "실적이 없는 구간도 0으로 채워 표의 줄이 빠지지 않게 합니다.",
      "기본은 확정 실적만 셉니다. 가실적을 포함하면 아직 검증되지 않은 톤수가 섞입니다.",
    ],
    related: [
      { label: "매출·매입 집계표", path: "/admin/settlement/summary", reason: "같은 수치를 업체별로 봅니다" },
      L.performance,
    ],
  },
];

// ---------------------------------------------------------------------------
// 기준정보
// ---------------------------------------------------------------------------

const MASTER: HelpTopic[] = [
  {
    pattern: "/admin/master/location",
    title: "기준정보 · 구간·장소 (장소)",
    screenshot: "/help/master-location.png",
    summary:
      "표준 장소명과 별칭을 관리하고, MES 업로드에서 수재 실적으로 가져올 도착지를 지정합니다.",
    steps: [
      "장소만 필요하면 ‘신규 등록’, 그 장소로 구간과 단가까지 한 번에 만들려면 ‘빠른 등록’ 을 씁니다.",
      "표에서 장소를 찾습니다. 새로 넣으려면 ‘신규 등록’ 을 눌러 입력 줄을 열고 ‘저장’ 합니다.",
      "같은 곳을 다르게 적는 표기가 있으면 그 행의 별칭에 더합니다.",
      "MES 수재 상행실적에 포함할 도착지는 `MES 수재 대상`을 ‘대상’으로 바꿉니다.",
    ],
    controls: [
      { name: "빠른 등록", description: "장소·구간·매출단가·매입단가를 한 모달에서 한 번에 만듭니다. 상차지·하차지는 기존 장소를 고르거나 새 이름을 넣으면 되고, 단가는 넣은 것만 만듭니다. 운송코드는 직접 적거나 `다음 번호` 를 눌러 추천 번호를 넣으며, 넣은 단가 셋에 함께 붙습니다. 매출 단가 옆 ‘청구처’ 를 고르면 그 거래처의 단가가 되고, 비우면 청구처 공통입니다. 같은 범위의 단가가 이미 있어도 적용 시작일이 다르면 등록되고(시작이 늦은 쪽이 그날부터 쓰입니다) 시작일까지 같을 때만 막습니다. 상·하차지와 방향이 이미 있는 구간이면 그 구간의 품목·청구처별 단가가 표로 나와 참고할 수 있습니다. 장소·구간·단가 세 화면 어디서 열어도 같습니다." },
      { name: "정리", description: "조회조건 맨 오른쪽입니다. ‘안 쓰는 것만’ 을 고르면 **지금 지울 수 있는 것만** 남습니다 — 600줄을 한 줄씩 눌러 보지 않아도 됩니다. ‘사용 여부’ 와 다른 축입니다: 이쪽은 *아무도 안 쓰는 것*, 저쪽은 *담당자가 목록에서 뺀 것*." },
      { name: "삭제", description: "**두 자리에 있습니다.** 행 오른쪽 ‘삭제’ 는 그 한 줄, 표 위 선택 줄의 ‘삭제’ 는 **체크한 줄 전부**를 지웁니다. 확인창의 ‘**관련 구간도 함께 지우기**’ 가 켜져 있어 그 장소를 쓰는 구간과 **그 구간의 단가까지** 함께 사라집니다 — 구간 탭으로 넘어가지 않아도 됩니다. 창이 **무엇이 몇 건 사라지는지 세어** 적고, 실적·배차에 걸려 못 지우는 구간은 줄을 그어 사유를 적습니다(그런 구간이 하나라도 있으면 그 **장소도 지워지지 않습니다**). 구간의 반대쪽 장소는 함께 지우지 않습니다 — 그 장소는 뒤에 ‘안 쓰는 것만’ 에 나타납니다. 끄면 구간이 막는 사유가 됩니다. 별칭은 함께 지워집니다. 되돌릴 수 없어 한 번 물어봅니다." },
      { name: "사용으로 / 미사용으로", description: "고른 행의 사용 여부를 한 번에 바꿉니다. 표 맨 앞 네모로 고르고, 머리글 네모는 보고 있는 페이지를 통째로 집습니다." },
      { name: "전체 N곳 선택", description: "표 위 조회조건에 **지금 걸어 두신 그 조건**에 맞는 전부를 집습니다 — 보고 있는 페이지가 아닙니다(그건 머리글 네모입니다). 걸린 조건은 표 바로 위 꼬리표로 보이고 숫자는 그 조건으로 조회된 건수와 같습니다. 아무것도 안 걸면 말 그대로 전부입니다. 페이지를 넘겨 가며 집지 않아도 됩니다." },
      {
        name: "장소 / 구간",
        description: "머리말 아래 탭입니다. 두 화면은 한 메뉴에 묶여 있고 여기서 오갑니다.",
      },
      { name: "신규 등록", description: "표 맨 위에 입력 줄을 엽니다. 다시 누르면 ‘신규 등록 취소’ 로 바뀌어 줄을 접습니다." },
      { name: "저장", description: "새로 넣은 줄을 저장합니다." },
      { name: "취소", description: "새로 넣던 줄을 버립니다." },
      { name: "대상 / 제외", description: "해당 장소를 MES 수재 상행실적 선별 대상에 포함하거나 제외합니다." },
      { name: "안내 닫기", description: "MES 업로드에서 넘어왔을 때 위에 뜨는 ‘이 표기를 별칭으로 넣으세요’ 안내를 닫습니다." },
    ],
    notes: [
      INLINE_EDIT,
      "장소와 구간은 사이드바 메뉴 하나에 묶여 있습니다. 머리말 아래 탭으로 오가세요.",
      "**장소를 ‘미사용’ 으로 돌리실 때는 그 장소를 쓰는 구간도 함께 옮겨 주세요.** 장소만 끄면 그 장소를 쓰는 구간은 그대로 살아 있어, 드롭다운에서는 사라졌는데 엑셀 업로드·배차는 계속 그 장소로 떨어지는 어긋난 상태가 됩니다. 남은 줄은 ‘구간’ 탭의 조회조건 ‘정리 › 미사용 장소 쓰는 것만’ 에서 한 번에 보실 수 있습니다.",
      "같은 곳이 이름만 달리 들어가 있으면 **한쪽을 지우지 말고** 이름을 고치거나 다른 쪽의 별칭으로 넣으세요 — 이름을 고치면 지난 실적의 표기까지 함께 바뀝니다.",
      "별칭은 전 장소를 통틀어 중복될 수 없습니다.",
      "송장이 자꾸 미매칭으로 남으면 그 표기를 별칭에 넣어 두면 다음부터 자동으로 붙습니다.",
      "MES 파일의 목적지 표기가 다르면 출처를 MES로 선택해 별칭에 추가하세요.",
      "MES 업로드의 ‘미해석’ 목적지를 누르면 이 화면으로 넘어옵니다. 그때는 위에 어떤 표기를 별칭으로 넣어야 하는지 안내가 뜨고, 검색어도 미리 채워집니다. 표기 원문 그대로는 검색되지 않으니(그래서 미해석입니다) 이름 앞부분으로 후보를 찾아 그 장소의 별칭에 더하세요.",
    ],
    related: [L.route, { label: "미매칭·중복·반려", path: "/admin/invoice/unmatched", reason: "장소 표기 때문에 안 붙은 송장을 확인합니다" }],
  },
  {
    pattern: "/admin/master/route",
    title: "기준정보 · 구간·장소 (구간)",
    screenshot: "/help/master-route.png",
    summary:
      "상차지→하차지 쌍과 방향, 기본 품목·톤수를 관리합니다. 단가와 가실적 기본값이 구간에 붙고, 청구처 열은 그 단가에서 유도해 보여 줍니다.",
    steps: [
      "새 구간은 ‘빠른 등록’ 이 가장 빠릅니다 — 장소가 없어도 여기서 함께 만들고 단가까지 붙입니다.",
      "같은 상차지·하차지·방향의 구간은 하나뿐입니다 — 품목이 달라도 새로 만들지 않고 고른 품목의 단가만 그 구간에 더합니다(2026-10-29). 배차가 자동으로 만들어 둔 미사용 구간이면 함께 사용으로 바꿉니다. 같은 기간·같은 품목의 공통 단가가 이미 있으면 막습니다 — 품목무관 단가만 있는 구간에 품목을 정한 단가를 넣는 것은 됩니다(그 품목에는 새 단가가 먼저 적용됩니다).",
      "표에서 구간을 찾습니다. 검색 칸은 상차지·하차지 이름과 **청구처 이름**으로 찾습니다. 새로 넣으려면 ‘신규 등록’ 을 눌러 입력 줄을 열고 ‘저장’ 합니다.",
      "자주 쓰는 품목·톤수를 기본값으로 채워 두면 배차 등록이 빨라집니다.",
      "구간 맨 앞 ‘펼치기’ 를 누르면 그 구간의 **품목별 단가**가 아래 줄로 나옵니다 — 한 줄이 계약 한 줄(매출 · 매입 개인 · 매입 업체)입니다. 여러 구간을 함께 펼칠 수 있고, 펼친 줄 맨 위에 각 칸의 이름이 적혀 있습니다. 단가 화면과 같은 단가라 어느 쪽에서 고쳐도 같습니다.",
    ],
    controls: [
      { name: "펼치기 / 접기", description: "구간 맨 앞 ‘단가’ 칸입니다. 펼치면 품목 · 청구처 · 요율 · 운송코드 · 매출단가 · 매입(개인) · 매입(업체) · 적용 시작 · 적용 종료 · 사용 순으로 단가 줄이 나오고 칸을 눌러 그 자리에서 고칩니다. **운송코드·적용기간·사용 여부는 그 줄의 단가 셋에 함께** 바뀌고, 금액 칸은 그 한 건만 바뀝니다. 흐리게 보이는 줄은 미사용이거나 적용 기간이 끝난 단가입니다. 업체·기사를 지정한 매입 단가는 이 줄에 담기지 않아 건수만 적습니다 — 단가 화면에서 보세요. 사용/미사용은 **단가 기준**입니다: 실적 계산은 단가의 사용 여부만 보고, 구간의 사용 여부는 드롭다운 목록 정리용입니다." },
      { name: "추가", description: "펼친 구간 머리줄 왼쪽에 있습니다. 누르면 새 단가 줄이 열립니다 — 품목(구간의 기본 품목이 들어와 있습니다)·청구처·운송코드·단가·적용기간을 적고 왼쪽 ‘저장’ 을 누릅니다. 넣은 단가만 만들어지고 셋이 같은 운송코드를 받습니다. 운송코드 칸 옆 번호 버튼은 다음 번호 추천입니다. 매입은 구간 공통 단가로 들어갑니다(업체·기사 지정은 단가 화면에서)." },
      { name: "정리", description: "조회조건 맨 오른쪽입니다. 두 가지를 찾습니다 — ‘안 쓰는 것만’ 은 **지금 지울 수 있는 것**이라 600줄을 한 줄씩 눌러 보지 않아도 됩니다. ‘미사용 장소 쓰는 것만’ 은 **고쳐야 하는 것**입니다: 구간은 살아 있는데 그 상차지·하차지가 미사용인 줄로, 해당 칸에 ‘미사용’ 꼬리표가 붙습니다. 장소를 정리하신 뒤 구간까지 옮겼는지 여기서 확인하세요. ‘사용 여부’ 와는 또 다른 축입니다: 그쪽은 *구간 자체가 꺼져 있는 것*입니다." },
      { name: "삭제", description: "**두 자리에 있습니다.** 행 오른쪽 ‘삭제’ 는 그 한 줄, 표 위 선택 줄의 ‘삭제’ 는 **체크한 줄 전부**를 지웁니다. **그 구간의 단가도 함께 지워집니다.** 확인창의 ‘**관련 장소도 함께 지우기**’ 가 켜져 있어 **이 구간만 쓰던** 상·하차지도 함께 사라집니다 — 장소 탭으로 넘어가지 않아도 됩니다. 다른 구간·배차·송장이 쓰는 장소는 그대로 남고, 창이 어느 장소가 열리는지 이름으로 적습니다. 쓰고 있는 구간은 건너뛴 뒤 사유를 알려드립니다. 되돌릴 수 없어 한 번 물어봅니다." },
      { name: "빠른 등록", description: "장소·구간·매출단가·매입단가를 한 모달에서 한 번에 만듭니다. 상차지·하차지는 기존 장소를 고르거나 새 이름을 넣으면 되고, 단가는 넣은 것만 만듭니다. 운송코드는 직접 적거나 `다음 번호` 를 눌러 추천 번호를 넣으며, 넣은 단가 셋에 함께 붙습니다. 매출 단가 옆 ‘청구처’ 를 고르면 그 거래처의 단가가 되고, 비우면 청구처 공통입니다. 같은 범위의 단가가 이미 있어도 적용 시작일이 다르면 등록되고(시작이 늦은 쪽이 그날부터 쓰입니다) 시작일까지 같을 때만 막습니다. 상·하차지와 방향이 이미 있는 구간이면 그 구간의 품목·청구처별 단가가 표로 나와 참고할 수 있습니다. 장소·구간·단가 세 화면 어디서 열어도 같습니다." },
      { name: "사용으로 / 미사용으로", description: "고른 행의 사용 여부를 한 번에 바꿉니다. 표 맨 앞 네모로 고르고, 머리글 네모는 보고 있는 페이지를 통째로 집습니다." },
      { name: "전체 N건 선택", description: "표 위 조회조건에 **지금 걸어 두신 그 조건**에 맞는 전부를 집습니다 — 보고 있는 페이지가 아닙니다(그건 머리글 네모입니다). 걸린 조건은 표 바로 위 꼬리표로 보이고 숫자는 그 조건으로 조회된 건수와 같습니다. 아무것도 안 걸면 말 그대로 전부입니다. 페이지를 넘겨 가며 집지 않아도 됩니다." },
      {
        name: "장소 / 구간",
        description: "머리말 아래 탭입니다. 두 화면은 한 메뉴에 묶여 있고 여기서 오갑니다.",
      },
      { name: "신규 등록", description: "표 맨 위에 입력 줄을 엽니다. 다시 누르면 ‘신규 등록 취소’ 로 바뀌어 줄을 접습니다." },
      { name: "저장", description: "새로 넣은 줄을 저장합니다." },
      { name: "취소", description: "새로 넣던 줄을 버립니다." },
      { name: "색", description: "표에서 이 구간 이름에 칠할 색입니다. 배차 등록의 구간 목록과 표, 하행 가실적에 그대로 나타나 구간이 서른 줄 넘게 늘어설 때 한눈에 갈립니다. 대표 15색은 예전 배차현황 시트에서 그대로 가져온 색이고, ‘직접 고르기’ 로 채도·명도를 조절해 다른 색도 만들 수 있습니다. 비워 두는 것이 기본입니다 — 자주 헷갈리는 구간만 칠하세요." },
      { name: "청구처", description: "이 구간의 매출을 청구할 거래처입니다. 고치는 자리가 아니라 매출단가에서 유도한 값이라 여기서는 보기만 합니다 — 바꾸시려면 기준정보 › 단가에서 그 구간의 거래처를 고치세요. ‘거래처 N곳’ 이면 후보가 둘 이상이라 청구처가 자동으로 정해지지 않습니다. 그 구간은 배차나 송장 검수에서 직접 골라 주셔야 합니다. ‘—’ 는 거래처를 지정한 매출단가가 없다는 뜻입니다." },
      { name: "가톤수", description: "이 구간의 가실적 기본값입니다. 하행 가실적·배차·일일실적이 이 값을 씁니다 — 비워 두면 29톤으로 잡힙니다. 단가 화면의 ‘가톤수’ 칸도 같은 값이고 저장되는 곳은 구간 한 곳입니다." },
      { name: "실적기준", description: "이 구간의 하행 실적을 상차 송장으로 마감할지 하차 송장으로 마감할지 정합니다. 기본은 ‘하차’ 입니다. 고른 쪽 송장 한 장이 검증되면 실적이 확정되고 확정 톤수도 그 송장에서 읽습니다. 상행 구간에는 송장이 없어 ‘—’ 입니다." },
      { name: "다시 계산", description: "실적기준을 바꾸면 물어봅니다. 기간을 고르면 그 구간의 확정 톤수를 바꾼 기준의 송장 값으로 다시 잡고 금액을 다시 냅니다. 결산이 확정된 실적과, 기준 송장이 아직 검증되지 않은 회차는 건드리지 않습니다." },
      { name: "나중에", description: "기준만 바꾸고 지난 실적은 그대로 둡니다. 앞으로 들어오는 송장부터 새 기준으로 확정됩니다." },
    ],
    notes: [
      INLINE_EDIT,
      "장소와 구간은 사이드바 메뉴 하나에 묶여 있습니다. 머리말 아래 탭으로 오가세요.",
      "실적기준을 바꿔도 이미 확정된 실적은 저절로 바뀌지 않습니다. 지난 기간을 맞추려면 ‘다시 계산’ 에서 기간을 고르세요 — 실수로 바꾼 체크가 지난달 명세서를 흔들면 안 되기 때문입니다.",
      "기사에게는 상차·하차 두 장을 계속 받습니다. 나중에 기준을 뒤집을 때 반대쪽 사진이 있어야 다시 계산할 수 있습니다.",
      "상행은 포스코 출발·수재가 기본값이지 제약이 아닙니다. 포스코 출입 정지 기간이나 수재 외 품목도 있어 상차지·품목을 바꿀 수 있습니다. 다만 상행 하차지가 포스코일 수는 없습니다(그건 하행입니다).",
      "‘신규 등록’ 으로 만들려면 양끝 장소가 먼저 있어야 합니다. ‘구간 빠른 등록’ 은 없는 장소를 함께 만듭니다.",
      "배차·엑셀 업로드가 처음 보는 상차지→하차지 조합을 미사용 구간으로 자동 등록합니다. 확인한 뒤 사용으로 바꾸세요 — 표 앞 네모로 여러 건을 한 번에 켤 수 있습니다.",
      "다만 **미사용 장소로는 구간을 만들지 않습니다.** 장소를 ‘미사용’ 으로 돌리신 것은 더 안 쓰겠다는 뜻이라, 그 장소로 구간이 또 생기면 정리하신 것이 되돌아갑니다. 그런 자료가 들어오면 그 실적은 구간 없이 남습니다 — 장소를 다시 사용으로 바꾸시거나 올바른 장소의 구간을 만들어 주세요.",
      "장소를 ‘미사용’ 으로 돌리실 때는 **그 장소를 쓰는 구간도 함께 옮겨 주세요.** 조회조건의 ‘정리 › 미사용 장소 쓰는 것만’ 이 남은 줄을 보여 줍니다.",
    ],
    related: [L.location, L.price, L.item],
  },
  {
    pattern: "/admin/master/item",
    title: "기준정보 · 품목",
    screenshot: "/help/master-item.png",
    summary:
      "표준 품목과, 원본 품목명(MES·배차 엑셀·송장 OCR)을 표준 품목으로 잇는 매핑을 관리합니다.",
    steps: [
      "‘표준품목’ 탭에서 쓰는 품목을 확인합니다.",
      "‘미분류’ 탭에 쌓인 원본명을 표준 품목에 이어 줍니다.",
      "하행 소계가 따로 필요한 품목은 ‘표준품목’ 의 ‘소계 분류’ 칸에서 고릅니다. 분류 자체를 더하려면 ‘소계 분류’ 탭에서 ‘신규 등록’ 합니다.",
      "‘품목매핑’ 탭에서 이미 이어 둔 매핑을 확인합니다.",
    ],
    controls: [
      { name: "사용으로 / 미사용으로", description: "고른 행의 사용 여부를 한 번에 바꿉니다. 표 맨 앞 네모로 고르고, 머리글 네모는 보고 있는 페이지를 통째로 집습니다." },
      { name: "전체 N종 선택", description: "표 위 조회조건에 **지금 걸어 두신 그 조건**에 맞는 전부를 집습니다 — 보고 있는 페이지가 아닙니다(그건 머리글 네모입니다). 걸린 조건은 표 바로 위 꼬리표로 보이고 숫자는 그 조건으로 조회된 건수와 같습니다. 아무것도 안 걸면 말 그대로 전부입니다. 페이지를 넘겨 가며 집지 않아도 됩니다." },
      { name: "표준품목 · 품목매핑 · 미분류 · 소계 분류", description: "품목 자체 / 이어 둔 매핑 / 아직 안 이은 원본명 / 하행 소계 분류를 나눠 보는 탭입니다." },
      { name: "기본으로", description: "‘소계 분류’ 탭에서 그 분류를 기본 분류로 지정합니다. 어디서도 정해지지 않은 하행 실적이 그 소계로 갑니다. 원래 기본은 저절로 풀립니다." },
      { name: "신규 등록", description: "표 맨 위에 입력 줄을 엽니다. 다시 누르면 ‘신규 등록 취소’ 로 바뀌어 줄을 접습니다." },
      { name: "저장", description: "새로 넣은 줄을 저장합니다." },
      { name: "취소", description: "새로 넣던 줄을 버립니다." },
    ],
    notes: [
      INLINE_EDIT,
      "미분류로 남은 원본명은 실적 집계에서 품목이 빈 것으로 취급됩니다. 비워 두지 마세요.",
      "수재는 상행 전용 품목이라 하행에서는 고를 수 없습니다.",
      "**‘표준품목’ 의 ‘소계 분류’ 칸은 그 품목의 하행 실적이 들어갈 소계의 기본값입니다.** 비우면 기본 분류(기타)입니다. 일일실적·월말 실적·집계표(분류별)가 이 값을 씁니다. 구간마다 다르게 잡는 것(예: 포스코로 가는 석회석분광만 석회석)은 ‘월말 실적’ 의 ‘분류 편집’ 에서 구간·품목으로 덮습니다. 단가와는 상관없습니다. ‘품목군’ 은 예전부터 있던 메모 칸이라 소계와는 상관없습니다.",
      "**‘소계 분류’ 탭은 분류 자체를 관리합니다** — 새 분류를 더하면 배포 없이 품목·월말 실적의 드롭다운과 실적·집계표의 소계가 생깁니다. ‘순서’ 가 작을수록 위에 섭니다. 이름을 바꾸면 이미 지정해 둔 것도 그 이름으로 따라옵니다.",
      "**‘소계 줄 색’ 은 일일실적·월말 실적에서 그 분류의 소계 줄 배경색입니다** — 소계가 눈에 잘 띄게 칠해 두세요. 구간 색과 같은 고르개이고, 비우면 옅은 회색입니다. 어두운 색을 고르면 글자가 흰색으로 바뀝니다.",
      "**기본 분류는 하나뿐입니다** — 어디서도 정해지지 않은 실적이 들어가는 곳(지금은 기타)이라 끄거나 지울 수 없습니다. 다른 분류를 ‘기본으로’ 지정하면 원래 기본은 풀립니다.",
      "분류를 **미사용**으로 바꾸면 그 분류로 정해 둔 실적은 다음 단계(품목의 분류 → 기본 분류)로 내려가고, 드롭다운에서도 빠집니다. **삭제는 아무 데서도 쓰지 않을 때만** 됩니다 — ‘쓰는 곳’ 열에 몇 군데서 쓰는지 보입니다.",
    ],
    related: [L.route, { label: "MES 상행실적 업로드", path: "/admin/performance/mes-upload", reason: "미분류 품목이 여기서 생깁니다" }],
  },
  {
    pattern: "/admin/master/vehicle",
    title: "기준정보 · 차량·기사",
    screenshot: "/help/master-vehicle.png",
    summary:
      "차량과, 차량에 붙는 기사·업체 배정 이력을 관리합니다. 차량번호는 전체·뒷 4자리·MES 코드 세 가지 표기를 함께 보관하며, 배차 등록에서 차량을 고르면 기사·업체가 자동으로 채워지는 근거가 여기입니다.",
    steps: [
      "‘차량’ 탭에서 차량번호와 MES 차량코드를 확인합니다.",
      "‘기사·업체 배정 이력’ 탭은 날짜 기준입니다. 위쪽 전일·다음일로 날짜를 옮기면 그 날 적용 중인 배정만 보입니다.",
      "기사나 업체가 바뀌면 그 날짜로 옮긴 뒤 표에서 바로 고칩니다. 이전 배정은 전날로 끝나고 그 날부터 새 배정이 시작됩니다.",
      "종료일이 없으므로 다음 날부터도 바꾼 내용을 그대로 이어받습니다. 매일 다시 넣을 필요가 없습니다.",
    ],
    controls: [
      { name: "차량 · 기사·업체 배정 이력", description: "차량 자체와 배정 이력을 나눠 보는 탭입니다." },
      { name: "사용으로 · 미사용으로", description: "골라 둔 차량의 사용 여부를 한 번에 바꿉니다. 미사용 차량은 배차 등록의 차량 목록에서 아래로 밀리고 ‘미사용’ 이 붙습니다." },
      { name: "운행가능 · 정비중 · 폐차", description: "골라 둔 차량의 운행 상태를 한 번에 바꿉니다." },
      { name: "전체 N대 선택", description: "표 위 조회조건에 **지금 걸어 두신 그 조건**에 맞는 전부를 집습니다 — 보고 있는 페이지가 아닙니다(그건 머리글 네모입니다). 걸린 조건은 표 바로 위 꼬리표로 보이고 숫자는 그 조건으로 조회된 건수와 같습니다. 아무것도 안 걸면 말 그대로 전부입니다. 페이지를 넘겨 가며 집지 않아도 됩니다." },
      { name: "선택 해제", description: "골라 둔 차량을 모두 놓습니다." },
      { name: "전일", description: "조회 일자를 하루 앞으로 옮깁니다." },
      { name: "다음일", description: "조회 일자를 하루 뒤로 옮깁니다." },
      { name: "이 날짜 기준", description: "고른 날짜에 적용 중인 배정만 봅니다. 누르면 ‘전체 이력’ 으로 바뀝니다." },
      { name: "신규 배정", description: "표 맨 위에 입력 줄을 엽니다. 다시 누르면 ‘신규 배정 취소’ 로 바뀌어 줄을 접습니다." },
      { name: "저장", description: "새로 넣은 줄을 저장합니다." },
      { name: "오늘로 종료", description: "적용 중인 배정을 오늘 날짜로 끝냅니다." },
      { name: "취소", description: "새로 넣던 줄을 버립니다." },
    ],
    notes: [
      INLINE_EDIT,
      "‘이 날짜 기준’ 에서 고치면 과거 배정은 그대로 남습니다. ‘전체 이력’ 에서 고치면 그 배정 기간 전체에 소급 적용되니 지난 배차가 참조한 값까지 바뀝니다.",
      "뒷 4자리는 차량번호에서 서버가 자동으로 계산하므로 직접 고칠 수 없습니다.",
      "같은 차량에 적용 기간이 겹치는 배정은 넣을 수 없습니다. 겹치면 확인 창이 뜹니다.",
      "MES 차량코드가 비어 있으면 MES 업로드에서 그 차량이 미매칭으로 남습니다.",
    ],
    related: [
      { ...L.dispatchOrders, reason: "여기 배정으로 기사·업체가 자동으로 채워집니다" },
      { label: "MES 상행실적 업로드", path: "/admin/performance/mes-upload", reason: "차량코드가 매칭 기준입니다" },
    ],
  },
  {
    pattern: "/admin/master/price",
    title: "기준정보 · 단가",
    screenshot: "/help/master-price.png",
    summary:
      "매출은 거래처(청구처)·구간·품목 기준, 매입은 구간·품목마다 개인 단가와 업체 단가를 한 줄에 함께 봅니다. 실적 금액이 전부 여기서 계산됩니다.",
    steps: [
      "‘매출단가’ / ‘매입단가’ 탭을 고릅니다.",
      "구간·품목·적용일자를 좁혀 지금 적용 중인 단가를 확인합니다.",
      "단가가 바뀌면 ‘신규 등록’ 으로 적용 시작일을 넣어 새 줄을 더합니다. 기존 줄은 이력으로 남깁니다.",
      "잘못 등록한 줄은 ‘삭제’ 로 치웁니다 — 이미 실적에 쓰인 단가는 지워지지 않으니 ‘미사용’ 으로 돌리세요.",
      "거래처마다 단가가 다르면 그 구간 줄에서 ‘추가’ 를 누릅니다 — 줄이 펼쳐지며 빈 줄이 하나 생기고, 거래처·단가를 적어 ‘저장’ 하면 들어갑니다. 구간·품목·기간을 다시 고르실 필요가 없습니다.",
    ],
    controls: [
      { name: "빠른 등록", description: "장소·구간·매출단가·매입단가를 한 모달에서 한 번에 만듭니다. 상차지·하차지는 기존 장소를 고르거나 새 이름을 넣으면 되고, 단가는 넣은 것만 만듭니다. 운송코드는 직접 적거나 `다음 번호` 를 눌러 추천 번호를 넣으며, 넣은 단가 셋에 함께 붙습니다. 매출 단가 옆 ‘청구처’ 를 고르면 그 거래처의 단가가 되고, 비우면 청구처 공통입니다. 같은 범위의 단가가 이미 있어도 적용 시작일이 다르면 등록되고(시작이 늦은 쪽이 그날부터 쓰입니다) 시작일까지 같을 때만 막습니다. 상·하차지와 방향이 이미 있는 구간이면 그 구간의 품목·청구처별 단가가 표로 나와 참고할 수 있습니다. 장소·구간·단가 세 화면 어디서 열어도 같습니다." },
      { name: "사용으로 / 미사용으로", description: "고른 단가의 사용 여부를 한 번에 바꿉니다. 단가를 껐다 켜면 그 기간 실적 금액이 다시 계산되고, 결과 문구에 재계산 건수가 함께 나옵니다." },
      { name: "전체 N구간 선택 / 전체 N건 선택", description: "표 위 조회조건에 **지금 걸어 두신 그 조건**에 맞는 전부를 집습니다 — 보고 있는 페이지가 아닙니다(그건 머리글 네모입니다). 걸린 조건은 표 바로 위 꼬리표로 보이고 숫자는 그 조건으로 조회된 건수와 같습니다. 아무것도 안 걸면 말 그대로 전부입니다. 페이지를 넘겨 가며 집지 않아도 됩니다." },
      { name: "매출단가 / 매입단가", description: "매출과 매입을 나눠 보는 탭입니다." },
      { name: "신규 등록", description: "표 맨 위에 입력 줄을 하나 더합니다. 여러 번 눌러 여러 줄을 쌓을 수 있습니다." },
      { name: "N줄 전체 저장", description: "쌓아 둔 입력 줄을 한 번에 넣습니다. 실패한 줄만 화면에 남아 고칠 수 있습니다." },
      { name: "저장 / 취소", description: "그 줄 하나만 넣거나 버립니다. 비고 왼쪽에 있습니다." },
      { name: "사용 / 미사용", description: "두 칸 중 **지금 상태**가 표시돼 있고, 누른 쪽 상태로 바뀝니다. 매입은 개인·업체 두 단가가 한 계약이라 함께 바뀝니다." },
      { name: "펼치기 / 접기", description: "매출단가 표는 **구간·품목마다 한 줄**입니다. 그 줄을 펼치면 거래처별 단가가 아래에 나옵니다 — **줄 아무 곳을 눌러도** 펼쳐지고, 체크상자를 누를 때는 펼쳐지지 않습니다. 거래처가 몇 곳인지와 단가 범위는 펼치지 않아도 그 줄에 적혀 있습니다. **거래처가 2곳 이상이면 청구처가 자동으로 정해지지 않아** 배차·송장 검수에서 골라 주셔야 합니다." },
      { name: "추가", description: "구간 줄에 있습니다. 누르면 그 구간이 펼쳐지고 **빈 줄 하나가 생깁니다.** 거래처·단가·적용기간을 적고 ‘저장’ 을 누르면 들어갑니다 — 저장하기 전에는 아무것도 바뀌지 않고, ‘취소’ 로 그 줄을 치울 수 있습니다. 구간·품목은 그 구간에서 따라오므로 다시 고르실 필요가 없습니다. 운송코드는 비워 두셔도 됩니다(매출은 한 코드에 한 건까지)." },
      { name: "삭제", description: "**한 번도 쓰지 않은 단가만** 지웁니다 — 잘못 등록해 바로 치우실 때 쓰세요. 그 기간·구간의 확정 실적이 한 건이라도 있으면 지워지지 않고 건수를 알려드립니다. 그때는 ‘미사용’ 으로 돌려 목록에서 빼세요. 되돌릴 수 없어 한 번 물어봅니다. 매입은 개인·업체 두 단가를 함께 지웁니다." },
    ],
    notes: [
      INLINE_EDIT,
      "톤당 요율은 톤수를, 회당 요율은 회차 수를 곱해 금액을 냅니다.",
      "공급가액 = 내림(톤수 × 단가), 부가세 = 내림(공급가액 ÷ 10) 입니다.",
      "시드 단가는 클라이언트 계약 단가표에서 그대로 온 값입니다. 운송코드 한 줄이 매출 단가 하나와 매입 단가 둘(개인·업체)을 낳습니다.",
      "매입 탭에서 요율이 두 번 나오는 것은 개인과 업체에 서로 다른 요율(톤당·회당)을 줄 수 있기 때문입니다.",
      "매입 단가의 빈 칸에 값을 넣으면 그쪽 단가를 새로 만듭니다. 구간·품목·적용기간은 옆 칸에서 그대로 가져옵니다.",
      "특정 기사·업체를 지정한 단가는 이 목록에 담기지 않습니다. 적용될 때는 구간 공통 단가보다 우선합니다.",
      "**운송코드는 마감 실적 업로드가 구간을 찾는 첫 열쇠입니다.** 엑셀에 적힌 코드를 여기 등록된 코드와 대조하므로, 코드가 맞으면 상차지·하차지 이름이 달라도 한 번에 붙습니다.",
      "**펼치면 지금 쓰이는 단가만 보입니다.** 적용 종료일이 지났거나, 같은 거래처에 더 늦게 시작한 단가가 들어와 밀려난 줄은 묶음 끝의 ‘지난 단가 N건 보기’ 를 눌러야 흐린 글씨로 나옵니다. 단가를 바꿀 때는 새 줄을 ‘추가’ 해 적용 시작일을 적으면 그날부터 옛 단가가 지난 단가로 내려갑니다. 기준일은 오늘이고, ‘적용일자’ 로 좁혀 보시면 그날입니다. 구간 줄의 단가 범위도 지금 단가로만 셉니다.",
      "구간 줄에도 운송코드가 보입니다. 거래처가 하나면 그 코드, 여럿이면 `a12 외 2` 처럼 첫 코드와 나머지 수를 적고, 마우스를 올리면 전부 보입니다. 코드를 고치려면 줄을 펼쳐 거래처 줄에서 고칩니다.",
      "**코드를 바꿀 때는 매출·매입을 함께 바꾸세요.** 해석이 매출 단가를 먼저 보고 없으면 매입 단가를 보는 순서라, 매출만 고치면 매입에 남은 옛 코드가 그대로 걸려 아무것도 안 바뀐 것처럼 보입니다. 짝 보기에서 고치면 두 건이 함께 옮겨집니다.",
      "새 줄의 운송코드 칸이 비어 있으면 옆에 **다음 번호**(예: `a734`)가 버튼으로 나옵니다. 누르면 그 번호가 들어가고, 직접 적으셔도 됩니다. 번호는 저장할 때 확정되므로 그 사이 다른 분이 같은 번호를 쓰면 저장이 막힙니다 — 버튼을 다시 누르면 새 번호가 나옵니다.",
      "구간이 비어 있는 단가의 운송코드는 쓰이지 않습니다. 코드로 구간을 찾는 것이므로 구간이 없으면 찾을 것이 없습니다.",
    ],
    related: [L.route, L.company, L.performance],
  },
  {
    pattern: "/admin/master/plan",
    title: "기준정보 · 월간 계획",
    screenshot: "/help/master-plan.png",
    summary:
      "월 단위 계획 물량을 넣는 화면입니다. 일일실적 현황의 계획(A)과 일일계획(C = 월 계획 ÷ 작업일수)이 여기서 나옵니다.",
    steps: [
      "월을 고릅니다.",
      "‘신규 등록’ 으로 줄을 열고 방향·품목·구간별 계획 물량을 넣은 뒤 ‘저장’ 합니다.",
      "한 달치를 한 번에 넣으려면 ‘월 단위 일괄 등록’ 을 씁니다.",
    ],
    controls: [
      { name: "신규 등록", description: "표 맨 위에 입력 줄을 엽니다. 다시 누르면 ‘신규 등록 취소’ 로 바뀌어 줄을 접습니다." },
      { name: "저장", description: "새로 넣은 줄을 저장합니다." },
      { name: "월 단위 일괄 등록", description: "여러 줄을 한 번에 넣는 창을 엽니다." },
      { name: "일괄 적용", description: "표 오른쪽 위 ‘작업일수 일괄’ 입니다. 칸에 일수(1~31)를 적고 누르면 **그 달 계획 전부의 작업일수**를 그 값으로 한 번에 바꾸고 일일 계획도 다시 계산됩니다. 방향을 골라 두었으면 그 방향만 바뀝니다." },
      { name: "취소", description: "새로 넣던 줄을 버립니다." },
    ],
    notes: [
      INLINE_EDIT,
      "(월, 방향, 품목, 구간) 조합은 중복될 수 없습니다.",
      "계획을 넣지 않으면 일일실적 현황의 계획 열이 비어 있습니다.",
    ],
    related: [L.performance, L.route, L.item],
  },
  {
    pattern: "/admin/master/profile",
    title: "기준정보 · 자사정보",
    screenshot: "/help/master-profile.png",
    summary:
      "거래명세서·세금계산서의 공급자 칸에 찍히는 우리 회사 정보와 직인을 넣는 화면입니다.",
    steps: [
      "등록번호·상호·성명·소재지주소·업태·종목을 채우고 ‘저장’ 합니다.",
      "매입 명세서에는 전화·팩스도 함께 찍히므로 같이 넣어 둡니다.",
      "‘이미지 올리기’ 로 직인을 등록합니다. 오른쪽에 미리보기가 나옵니다.",
    ],
    controls: [
      { name: "저장", description: "입력한 값을 저장합니다. 다음에 발급하는 명세서부터 이 값이 찍힙니다." },
      { name: "이미지 올리기", description: "직인 이미지를 올립니다. PNG·JPG, 2MB 이하입니다." },
      { name: "다른 이미지로 바꾸기", description: "이미 등록된 직인을 새 이미지로 갈아끼웁니다. 이전 파일은 남지 않습니다." },
      { name: "직인 삭제", description: "직인을 지웁니다. 이후 명세서는 도장 없이 나갑니다." },
    ],
    notes: [
      "매출 명세서는 등록번호·상호·성명·소재지주소·업태·종목을 쓰고, 매입 명세서는 전화·팩스를 더 씁니다.",
      "이미 발급한 문서는 바뀌지 않습니다. 발급 시점의 값이 파일에 박혀 있습니다. 고친 값을 반영하려면 명세서를 다시 만드세요.",
      "직인은 모든 거래명세서에 찍힙니다 — 매출은 공급받는자 칸에 겹쳐, 매입(업체·개인 차주)은 ‘귀하’ 옆에 찍힙니다. 배경이 투명한 PNG 를 권장합니다. 긴 쪽이 72px 보다 작은 이미지는 올린 크기 그대로 들어갑니다.",
      "저장한 적이 없으면 서버 기본값을 씁니다. 화면 위에 안내가 뜨면 값을 확인하고 한 번 저장해 두세요.",
    ],
    related: [
      { label: "거래명세서·세금계산서", path: "/admin/settlement", reason: "여기 값이 명세서에 찍힙니다" },
      L.company,
    ],
  },
];

// ---------------------------------------------------------------------------
// 기사관리 — 기사가 올린 것을 담당자가 받아 처리하는 화면들
// ---------------------------------------------------------------------------

const DRIVERS: HelpTopic[] = [
  {
    pattern: "/admin/drivers/disputes",
    title: "이의신청 회신",
    screenshot: "/help/drivers-disputes.png",
    summary:
      "기사가 월 정산에 제기한 이의를 확인하고 회신하는 화면입니다. 회신 내용은 기사 앱 실적·정산 화면에 그대로 보입니다.",
    steps: [
      "미처리 건이 목록 위에 옵니다. 상태·유형·대상월로 좁힐 수 있습니다.",
      "바로 답하기 어려우면 ‘검토중으로’ 를 눌러 표시해 둡니다.",
      "‘회신’ 을 눌러 원문 전체를 보며 답변을 씁니다.",
      "‘처리 후 상태’ 를 완료로 두고 저장하면 기사에게 알림이 갑니다.",
    ],
    controls: [
      { name: "검토중으로", description: "접수된 건을 검토중으로 표시합니다. 기사에게 알림은 가지 않습니다." },
      { name: "회신", description: "회신 모달을 엽니다. 신청 원문 전체를 함께 보여 줍니다." },
      { name: "회신 수정", description: "이미 완료한 건의 회신 내용만 고칩니다." },
      { name: "저장", description: "회신을 저장합니다. 이 모달의 주 동작입니다." },
    ],
    notes: [
      "완료 처리하려면 회신 내용이 반드시 있어야 합니다.",
      "완료된 건을 접수·검토중으로 되돌릴 수는 없습니다. 회신 내용만 고칠 수 있고, 처리 일시는 최초 완료 시점 그대로 남습니다.",
      "기사가 다시 다투려면 새 이의신청을 올려야 합니다.",
    ],
    related: [L.performance, L.price, L.driverAccounts],
  },
  {
    pattern: "/admin/drivers/vehicle-changes",
    title: "차량변경 승인",
    screenshot: "/help/drivers-vehicle-changes.png",
    summary:
      "기사가 올린 차량 변경 요청을 승인하거나 반려하는 결재 화면입니다. 승인하면 기존 배정이 적용일 전날로 끝나고 적용일부터 새 배정이 자동으로 만들어집니다.",
    steps: [
      "처음 열면 ‘접수’ 건만 보입니다.",
      "‘승인’ 을 눌러 확정 차량과 적용일을 확인합니다.",
      "희망 차량번호와 맞는 차량을 서버가 찾아 두지만, 최종 확정은 직접 고릅니다.",
      "모달 아래 안내로 배정이 어떻게 바뀌는지 확인한 뒤 승인합니다.",
      "받아 줄 수 없으면 ‘반려’ 로 사유를 적습니다.",
    ],
    controls: [
      { name: "승인", description: "확정 차량·적용일을 정하는 모달을 엽니다." },
      { name: "반려", description: "사유를 적어 돌려보냅니다. 사유는 기사 화면에 그대로 보입니다." },
      { name: "확정 차량", description: "기준정보의 차량 목록에서 고릅니다. 검색이 됩니다." },
      { name: "적용일", description: "이 날짜부터 새 배정이 적용됩니다. 기본값은 기사 희망일입니다." },
    ],
    notes: [
      "표에서 값을 직접 고칠 수 없습니다. 배정을 만드는 조작이라 실수로 눌리지 않게 모달로만 받습니다.",
      "희망 차량번호는 기사가 손으로 적은 값이라 오타가 있을 수 있습니다. 미확인으로 표시되면 직접 고르세요.",
      "그 차량이 같은 기간에 다른 기사에게 배정돼 있으면 승인이 막힙니다. 이때 기존 배정은 건드려지지 않은 채 그대로 남습니다.",
      "승인·반려한 건은 되돌릴 수 없습니다. 기사가 다시 신청해야 합니다.",
    ],
    related: [L.vehicle, L.driverAccounts],
  },
  {
    pattern: "/admin/drivers/notices",
    title: "공지 관리",
    screenshot: "/help/drivers-notices.png",
    summary:
      "기사 앱 내 정보 화면 아래에 뿌리는 공지입니다. 게시해야 기사에게 보이고, 게시를 중지하면 즉시 사라집니다.",
    steps: [
      "오른쪽 위 ‘공지 작성’ 으로 제목과 본문을 씁니다.",
      "만들면 기본은 미게시입니다. 내용을 확인한 뒤 ‘게시’ 를 누릅니다.",
      "중요한 공지는 고정을 ‘예’ 로 두면 기사 화면 맨 위에 붙습니다.",
      "내릴 때는 ‘게시 중지’ 를 누릅니다.",
    ],
    controls: [
      { name: "공지 작성", description: "제목·본문을 쓰는 모달을 엽니다. 이 화면의 주 동작입니다." },
      { name: "본문 수정", description: "이미 만든 공지의 제목·본문·고정을 고칩니다." },
      { name: "게시 / 게시 중지", description: "기사 앱 노출을 켜고 끕니다. 한 번 확인을 묻습니다." },
      { name: "삭제", description: "공지를 지웁니다. 되돌릴 수 없습니다." },
    ],
    notes: [
      "제목과 고정 여부는 표에서 바로 고칠 수 있습니다. 본문은 길어서 모달에서만 받습니다.",
      "목록 순서는 기사 화면과 같습니다 — 고정 공지가 먼저, 그 다음 최근 게시순.",
      "공지를 게시해도 기사에게 알림은 가지 않습니다. 기사가 내 정보 화면에서 봅니다.",
    ],
    related: [L.driverAccounts],
  },
  {
    pattern: "/admin/drivers/accounts",
    title: "기사 계정",
    screenshot: "/help/drivers-accounts.png",
    summary:
      "기사 로그인 계정을 관리합니다. 로그인 ID 는 기사 코드이며, 비밀번호를 발급해야 기사가 기사 웹에 로그인할 수 있습니다.",
    steps: [
      "‘기사 추가’ 로 표 맨 위에 입력 줄을 열고 로그인 ID·이름·유형을 채웁니다. 로그인 ID 는 그 기사가 모는 차량번호 뒷 4자리로 정합니다 — 외우기 쉬운 값이어야 합니다.",
      "자체 인증이면 초기 비밀번호(4자 이상)를 함께 넣고 저장합니다. 비우면 사번과 같게 만듭니다.",
      "기존 기사의 비밀번호는 ‘비밀번호 관리’ 에서 바꾸거나 사번으로 되돌립니다.",
      "그만둔 기사는 ‘상태 변경’ 으로 퇴사·휴직으로 바꿉니다.",
    ],
    controls: [
      { name: "명세서 거래처", description: "개인 차주 거래명세서의 **공급자 칸**(등록번호·상호·성명·주소·업태·종목)이 이 거래처에서 옵니다. 기준정보 › 거래처에 차주의 사업자를 먼저 등록하고 여기서 고릅니다 — 사업자번호가 없으면 비워 둔 채 상호·주소만 적어도 됩니다. **소속 운송사와 다릅니다** — 소속 운송사는 매입을 어디로 치를지 정하므로 여기 넣을 값을 거기 넣으면 기사 앞 매입이 그 거래처로 옮겨 갑니다." },
      { name: "기사 추가", description: "표 맨 위에 입력 줄을 엽니다." },
      { name: "저장", description: "새로 넣은 줄을 저장합니다." },
      { name: "비밀번호 관리", description: "비밀번호를 바꾸거나 사번으로 되돌립니다. 인증 출처가 ‘1차’ 인 기사는 누를 수 없습니다." },
      { name: "변경", description: "입력한 새 비밀번호로 바꿉니다. 4자 이상이어야 하고, 저장 후 다시 볼 수 없으니 기사에게 직접 전달하세요." },
      { name: "사번으로 초기화", description: "입력값을 쓰지 않고 비밀번호를 사번과 같게 되돌립니다. 기사에게 따로 알려 줄 값이 없어 가장 간단합니다." },
      { name: "상태 변경", description: "활성·휴직·퇴사를 바꿉니다. 사유를 적으면 감사 로그에 남습니다." },
    ],
    notes: [
      INLINE_EDIT,
      "계정을 지우는 기능은 없습니다. 지난 실적과 감사 로그가 그 계정을 참조하므로 상태를 퇴사로 바꾸세요.",
      "그만둔 기사는 목록 **맨 아래로 모입니다** — 활성 → 휴직 → 퇴사 순이고 그 묶음 위에 선이 그어집니다. 쓰고 있는 기사만 보시려면 ‘상태’ 를 ‘활성’ 으로 거르세요.",
      "휴직·퇴사 상태의 기사는 기사 웹에 로그인할 수 없습니다.",
      "인증 출처가 ‘1차’ 인 기사의 비밀번호는 1차 시스템이 관리하므로 여기서 바꿀 수 없습니다.",
      "로그인 ID(기사 코드)는 만든 뒤 바꾸지 않습니다. 기사가 외운 값이고 감사 로그가 참조합니다.",
    ],
    related: [L.vehicle, L.disputes, L.vehicleChanges],
  },
];

// ---------------------------------------------------------------------------
// 시스템
// ---------------------------------------------------------------------------

const SYSTEM: HelpTopic[] = [
  {
    pattern: "/admin/audit-logs",
    title: "변경 이력",
    screenshot: "/help/audit-logs.png",
    summary:
      "누가 언제 무엇을 바꿨는지 남은 기록을 봅니다. 배차·실적·단가·계정처럼 시스템이 다루는 거의 모든 것이 바뀔 때 한 줄씩 쌓입니다. 조회 전용이라 기록을 고치거나 지울 수는 없습니다.",
    steps: [
      "계정 · 행위 · 대상 · 날짜로 좁힙니다. 비워 두면 전체가 최근 순으로 나옵니다.",
      "찾는 조작이 기억나지 않으면 ‘검색’ 에 계정 이름이나 사유를 넣습니다.",
      "줄을 누르면 그 조작이 바꾼 칸이 ‘바뀐 것 → 바뀐 값’ 으로 펼쳐집니다.",
    ],
    controls: [
      { name: "계정", description: "**이력에 남은 계정 전부**가 나옵니다. 사용자 관리에서 지운 계정도 ‘(삭제된 계정)’ 으로 함께 나옵니다 — 그 계정이 한 일을 확인하실 수 있습니다. ‘시스템’ 은 사람이 아니라 자동 처리입니다(MES 반영이 만든 배차 같은 것)." },
      { name: "행위", description: "실제로 기록된 것만 나옵니다. ‘로그인’ 이 가장 많아 변경만 보시려면 다른 행위를 고르시는 편이 빠릅니다." },
      { name: "대상", description: "무엇이 바뀌었는지입니다 — 배차 · 실적 · 단가 · 계정 같은 것입니다." },
      { name: "변경", description: "펼치면 보일 칸 수입니다. ‘—’ 인 줄은 펼칠 것이 없습니다(로그인처럼 바뀐 칸이 아예 없는 기록입니다)." },
      { name: "사유·경위", description: "왜 이 줄이 생겼는지입니다. 담당자가 적은 사유일 때도 있고(확정 취소), 어떤 작업이 만든 줄인지일 때도 있습니다(‘엑셀 업로드 반영’ · ‘구간 빠른 등록’)." },
    ],
    notes: [
      "일시는 보시는 컴퓨터의 시각으로 나오고, ‘시작일’·‘종료일’ 은 **한국 날짜** 기준입니다.",
      "한 번 누른 조작이 여러 줄을 만들면 그 줄들은 일시가 같아 표에서 붙어 나옵니다. 같은 ‘사유·경위’ 를 ‘검색’ 에 넣으면 한 묶음으로 보실 수 있습니다.",
      "펼쳤을 때 ‘바뀐 칸이 없습니다’ 라고 나오는 줄이 있습니다. 같은 값으로 저장하면 그렇게 남고, 기록 자체는 정상입니다.",
      "칸 이름이 영문으로 나오는 것이 있습니다. 시스템 내부 이름이라 그대로 두었습니다 — 개발자에게 그 이름을 알려 주시면 바로 찾습니다.",
      "**조회 전용입니다.** 이 화면에서 기록을 고치거나 지울 수 있는 길은 없습니다.",
    ],
    related: [L.users, L.driverAccounts, L.company],
  },
];

// ---------------------------------------------------------------------------
// 조회
// ---------------------------------------------------------------------------

export const HELP_TOPICS: HelpTopic[] = [
  ...DISPATCH,
  ...PERFORMANCE,
  ...INVOICE,
  ...SETTLEMENT,
  ...MASTER,
  ...DRIVERS,
  ...SYSTEM,
];

/** ‘/admin/dispatch/:id/edit’ 같은 패턴을 실제 경로와 맞춘다 */
function matches(pattern: string, pathname: string): boolean {
  const p = pattern.split("/").filter(Boolean);
  const a = pathname.split("/").filter(Boolean);
  if (p.length !== a.length) return false;
  return p.every((segment, i) => segment.startsWith(":") || segment === a[i]);
}

/**
 * 현재 경로의 도움말을 찾는다.
 * 구체적인 패턴(동적 구간이 적은 쪽)을 먼저 고른다 — ‘/admin/dispatch/orders’ 가
 * ‘/admin/dispatch/:id’ 류보다 앞서야 한다.
 */
export function findHelpTopic(pathname: string): HelpTopic | null {
  const found = HELP_TOPICS.filter((topic) => matches(topic.pattern, pathname));
  if (found.length === 0) return null;
  return found.sort(
    (a, b) =>
      a.pattern.split("/").filter((s) => s.startsWith(":")).length -
      b.pattern.split("/").filter((s) => s.startsWith(":")).length
  )[0];
}
